import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useGameStore, Companion } from '../store/gameStore';
import { storageManager } from '../utils/storageManager';

interface StockMarketModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface StockItem {
  id: string;
  name: string;
  symbol: string;
  currentPrice: number;
  previousPrice: number;
  change: number;
  changePercent: number;
  volume: number;
  type: 'coin' | 'gem';
  icon: string;
  priceHistory: Array<{ timestamp: number; price: number }>;
  marketCap: number;
  high24h: number;
  low24h: number;
  description: string;
  sector: string;
}

interface ChartData {
  labels: string[];
  datasets: Array<{
    label: string;
    data: number[];
    borderColor: string;
    backgroundColor: string;
    tension: number;
  }>;
}

// Neues Chart Modal Component
const ChartModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  stock: StockItem | null;
  formatNumber: (num: number) => string;
}> = ({ isOpen, onClose, stock, formatNumber }) => {
  // Vereinfacht: Nur ein Chart ohne Timeframe-Auswahl
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [currentStock, setCurrentStock] = useState<StockItem | null>(null);

  // Initialisiere mit aktueller Aktie
  useEffect(() => {
    if (stock) {
      setCurrentStock(stock);
    }
  }, [stock]);

  // Chart zeichnen
  useEffect(() => {
    if (!currentStock || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Canvas-Größe setzen
    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;

    // Chart zeichnen
    drawChart(ctx, currentStock.priceHistory, currentStock.name);
  }, [currentStock]);

  // Chart verwendet das vom Parent übergebene `stock`-Objekt (Parent aktualisiert `chartStock` jede Sekunde)
  useEffect(() => {
    if (!stock) return;
    setCurrentStock(stock);
  }, [stock]);

  const drawChart = (ctx: CanvasRenderingContext2D, priceHistory: Array<{timestamp: number, price: number}>, stockName: string) => {
    const width = ctx.canvas.width;
    const height = ctx.canvas.height;
    
    // Sortiere Daten nach Timestamp
    const sortedHistory = [...priceHistory].sort((a, b) => a.timestamp - b.timestamp);
    
    // Verwende nur die letzten 100 Datenpunkte für optimale Performance
    const chartData = sortedHistory.slice(-100);
    
    if (chartData.length >= 2) {
      drawChartData(ctx, chartData, width, height);
    }
  };

  const drawChartData = (ctx: CanvasRenderingContext2D, data: Array<{timestamp: number, price: number}>, width: number, height: number) => {
    const prices = data.map(p => p.price);
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    const priceRange = maxPrice - minPrice;
    const currentPrice = data[data.length - 1]?.price || 0;
    const startPrice = data[0]?.price || 0;
    const priceChange = currentPrice - startPrice;
    const priceChangePercent = startPrice > 0 ? (priceChange / startPrice) * 100 : 0;
    
    // Kein Zoom-Out - direkte Darstellung
    const adjustedMinPrice = minPrice;
    const adjustedMaxPrice = maxPrice;
    const adjustedPriceRange = maxPrice - minPrice;
    
    // Kein Padding - vollständige Chart-Nutzung
    const xPadding = 0;
    const yPadding = 0;
    const chartWidth = width;
    const chartHeight = height;
    
    // Hintergrund löschen
    ctx.clearRect(0, 0, width, height);
    
    // Gradient-Hintergrund
    const gradient = ctx.createLinearGradient(0, 0, 0, height);
    gradient.addColorStop(0, 'rgba(17, 24, 39, 0.8)');
    gradient.addColorStop(1, 'rgba(17, 24, 39, 0.4)');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);
    
    // Grid zeichnen
    ctx.strokeStyle = 'rgba(55, 65, 81, 0.6)';
    ctx.lineWidth = 0.5;
    
    // Horizontale Linien
    for (let i = 0; i <= 5; i++) {
      const y = yPadding + (chartHeight * i) / 5;
      ctx.beginPath();
      ctx.moveTo(xPadding, y);
      ctx.lineTo(width - xPadding, y);
      ctx.stroke();
    }
    
    // Vertikale Linien
    for (let i = 0; i <= 8; i++) {
      const x = xPadding + (chartWidth * i) / 8;
      ctx.beginPath();
      ctx.moveTo(x, yPadding);
      ctx.lineTo(x, height - yPadding);
      ctx.stroke();
    }
    
    // Preislinie (ohne Stärke)
    ctx.strokeStyle = '#3B82F6';
    ctx.lineWidth = 0;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    
    data.forEach((point, index) => {
      const x = xPadding + (index / (data.length - 1)) * chartWidth;
      const y = height - yPadding - ((point.price - adjustedMinPrice) / adjustedPriceRange) * chartHeight;
      
      if (index === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    });
    
    ctx.stroke();
    
    // Füllung unter der Linie
    const fillGradient = ctx.createLinearGradient(0, 0, 0, height);
    fillGradient.addColorStop(0, 'rgba(59, 130, 246, 0.3)');
    fillGradient.addColorStop(1, 'rgba(59, 130, 246, 0.05)');
    
    ctx.fillStyle = fillGradient;
    ctx.beginPath();
    ctx.moveTo(xPadding, height - yPadding);
    
    data.forEach((point, index) => {
      const x = xPadding + (index / (data.length - 1)) * chartWidth;
      const y = height - yPadding - ((point.price - adjustedMinPrice) / adjustedPriceRange) * chartHeight;
      ctx.lineTo(x, y);
    });
    
    ctx.lineTo(width - xPadding, height - yPadding);
    ctx.closePath();
    ctx.fill();
    
    // Aktuelle Position markieren
    if (data.length > 0) {
      const lastPoint = data[data.length - 1];
      const x = width - 5;
      const y = height - ((lastPoint.price - adjustedMinPrice) / adjustedPriceRange) * chartHeight;
      
      ctx.shadowColor = '#3B82F6';
      ctx.shadowBlur = 10;
      ctx.fillStyle = '#3B82F6';
      ctx.beginPath();
      ctx.arc(x, y, 6, 0, 2 * Math.PI);
      ctx.fill();
      
      ctx.shadowBlur = 0;
      ctx.fillStyle = '#FFFFFF';
      ctx.beginPath();
      ctx.arc(x, y, 3, 0, 2 * Math.PI);
      ctx.fill();
    }
    
    // Achsen-Legenden zeichnen
    drawAxisLabels(ctx, width, height, data, {
      xPadding,
      yPadding,
      chartHeight,
      adjustedMinPrice,
      adjustedPriceRange,
      currentPrice,
      minPrice,
      maxPrice,
      timeframe: 'realtime'
    });
  };

  const drawAxisLabels = (ctx: CanvasRenderingContext2D, width: number, height: number, chartData: Array<{timestamp: number, price: number}>, chartInfo: {
    xPadding: number;
    yPadding: number;
    chartHeight: number;
    adjustedMinPrice: number;
    adjustedPriceRange: number;
    currentPrice: number;
    minPrice: number;
    maxPrice: number;
    timeframe: string;
  }) => {
    const { xPadding, yPadding, chartHeight, adjustedMinPrice, adjustedPriceRange, currentPrice, minPrice, maxPrice, timeframe } = chartInfo;
    
    // Y-Achse (Preis) Labels - rechts (mobil-optimiert)
    ctx.font = 'bold 14px Arial';
    ctx.fillStyle = '#FFFFFF';
    ctx.textAlign = 'left';
    
    // Max Preis (oben rechts)
    ctx.fillStyle = '#10B981';
    ctx.fillText(formatNumber(maxPrice), width - 75, 25);
    
    // Min Preis (unten rechts)
    ctx.fillStyle = '#EF4444';
    ctx.fillText(formatNumber(minPrice), width - 75, height - 25);
    
    // Aktueller Preis (an der aktuellen Position)
    if (chartData.length > 0) {
      const lastPoint = chartData[chartData.length - 1];
      const y = height - ((lastPoint.price - adjustedMinPrice) / adjustedPriceRange) * chartHeight;
      
      ctx.fillStyle = '#3B82F6';
      ctx.fillText(formatNumber(currentPrice), width - 75, y + 5);
    }
    
    // X-Achse (Zeit) Labels - leer gelassen
    // Keine Uhrzeiten an der X-Achse
    
    // Timeframe Label (oben links)
    ctx.textAlign = 'left';
    ctx.font = 'bold 14px Arial';
    ctx.fillStyle = '#3B82F6';
    ctx.fillText(`${timeframe.toUpperCase()}`, 10, 25);
  };



  const getTimeRangeText = (timeframe: string, dataLength: number) => {
    switch (timeframe) {
      case '1h':
        return `Zeitraum: 1 Std. (${dataLength} Min.)`;
      case '1d':
        return `Zeitraum: 4 Std. (${dataLength} Min.)`;
      default:
        return `Zeitraum: ${dataLength} Punkte`;
    }
  };

  const getChangeColor = (change: number) => {
    return change >= 0 ? 'text-green-400' : 'text-red-400';
  };

  const getChangeIcon = (change: number) => {
    return change >= 0 ? '↗️' : '↘️';
  };

  // Hilfsfunktion zum Filtern der Datenpunkte für verschiedene Zeiträume
  const getFilteredDataForTimeframe = (priceHistory: Array<{timestamp: number, price: number}>, timeframe: string) => {
    const now = Date.now();
    let timeRange: number;
    
    switch (timeframe) {
      case '1h':
        timeRange = 60 * 60 * 1000; // 1 Stunde
        break;
      case '1d':
        timeRange = 4 * 60 * 60 * 1000; // 4 Stunden (umbenannt zu 1d)
        break;
      default:
        timeRange = 60 * 60 * 1000; // Standard: 1 Stunde
    }
    
    const cutoffTime = now - timeRange;
    return priceHistory.filter(point => point.timestamp >= cutoffTime);
  };

  if (!stock) return null;

  return (
    <AnimatePresence key="chart-modal">
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0, y: 50 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0, y: 50 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="bg-gradient-to-br from-gray-900 via-blue-900 to-indigo-900 rounded-2xl border-2 border-blue-500/50 shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-6 border-b border-blue-500/30 bg-gradient-to-r from-blue-900/50 to-indigo-900/50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{currentStock?.icon || stock.icon}</span>
                  <div>
                    <h3 className="text-lg font-bold text-white truncate">{currentStock?.name || stock.name}</h3>
                    <div className="text-xs text-gray-400">{currentStock?.symbol || stock.symbol} • {currentStock?.sector || stock.sector}</div>
                  </div>
                </div>
                <motion.button
                  onClick={onClose}
                  className="text-gray-400 hover:text-white text-2xl transition-colors"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  ✕
                </motion.button>
              </div>
              <div className="mt-2 flex items-center justify-between">
                <div className="text-left">
                  <div className="text-2xl font-bold text-white">{formatNumber(currentStock?.currentPrice || 0)}</div>
                  <div className={`text-sm ${getChangeColor(currentStock?.changePercent || 0)}`}>
                    {getChangeIcon(currentStock?.changePercent || 0)} {(currentStock?.changePercent || 0).toFixed(2)}%
                  </div>
                </div>
                <div className="text-xs text-gray-400 text-right">
                  <div>24h: {formatNumber(currentStock?.low24h || 0)}-{formatNumber(currentStock?.high24h || 0)}</div>
                  <div>Vol: {formatNumber(currentStock?.volume || 0)}</div>
                  <div>MK: {formatNumber((currentStock?.currentPrice || 0) * (currentStock?.volume || 0))}</div>
                </div>
              </div>
              

            </div>

            {/* Chart Content */}
            <div className="p-6">
                                    {/* Timeframe Selector - Mobil optimiert */}
                      {/* Timeframe-Auswahl entfernt - nur noch ein Chart */}

              {/* Chart */}
              <div className="bg-gray-800/50 rounded-xl border border-gray-600 p-4 mb-6">
                <canvas
                  ref={canvasRef}
                  className="w-full h-80"
                />
              </div>

              {/* Market Stats - Mobil optimiert mit Live-Updates */}
              <div className="flex flex-wrap gap-2 justify-center">
                <div className="flex-1 min-w-0 p-3 bg-gray-800/50 rounded-lg border border-gray-600 text-center">
                  <div className="text-xs text-gray-400">High</div>
                  <div className="text-sm font-bold text-green-400 truncate">
                    {formatNumber(currentStock?.high24h || 0)}
                  </div>
                </div>
                <div className="flex-1 min-w-0 p-3 bg-gray-800/50 rounded-lg border border-gray-600 text-center">
                  <div className="text-xs text-gray-400">Low</div>
                  <div className="text-sm font-bold text-red-400 truncate">
                    {formatNumber(currentStock?.low24h || 0)}
                  </div>
                </div>
                <div className="flex-1 min-w-0 p-3 bg-gray-800/50 rounded-lg border border-gray-600 text-center">
                  <div className="text-xs text-gray-400">Vol</div>
                  <div className="text-sm font-bold text-white truncate">
                    {formatNumber(currentStock?.volume || 0)}
                  </div>
                </div>
                <div className="flex-1 min-w-0 p-3 bg-gray-800/50 rounded-lg border border-gray-600 text-center">
                  <div className="text-xs text-gray-400">MK</div>
                  <div className="text-sm font-bold text-white truncate">
                    {formatNumber((currentStock?.currentPrice || 0) * (currentStock?.volume || 0))}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Neues Trading Modal Component
const TradingModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  stock: StockItem | null;
  onTrade: (stock: StockItem, amount: number, type: 'buy' | 'sell', currency: 'coins' | 'gems') => void;
  availableCoins: number;
  availableGems: number;
  formatNumber: (num: number) => string;
  stockMarket: StockItem[];
}> = ({ isOpen, onClose, stock, onTrade, availableCoins, availableGems, formatNumber, stockMarket }) => {
  
  // Funktion zur Berechnung verfügbarer Aktien
  const getAvailableShares = (stockId: string) => {
    // Verwende die gleiche Logik wie im Hauptmodal
    const getBaseSharesForStock = (id: string) => {
      // Konsistente Zufallszahl basierend auf Stock-ID
      let hash = 0;
      for (let i = 0; i < id.length; i++) {
        const char = id.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32bit integer
      }
      const random = Math.abs(hash) / 2147483647; // Normalize to 0-1
      
      // Anteile zwischen 1M und 1B basierend auf Aktien-Typ
      const stock = stockMarket.find(s => s.id === id);
      if (stock?.type === 'gem') {
        // Diamanten-Aktien: 1M - 10M Anteile
        return Math.floor(1000000 + random * 9000000);
      } else {
        // Münzen-Aktien: 100M - 1B Anteile
        return Math.floor(100000000 + random * 900000000);
      }
    };
    
    // Spezialfall: Auto Gräber Xdustries hat unbegrenzte verfügbare Anteile
    if (stockId === 'auto_graber_xdustries') {
      return Infinity;
    }
    const baseShares = getBaseSharesForStock(stockId);
    const state = useGameStore.getState();
    const globalSharesOwned = state.stockMarket.globalMarket.totalSharesOwned[stockId] || 0;
    return Math.max(0, baseShares - globalSharesOwned);
  };
  const [tradeAmount, setTradeAmount] = useState<number>(0);
  const [selectedCurrency, setSelectedCurrency] = useState<'coins' | 'gems'>('coins');

  const getAvailableCurrency = () => {
    return selectedCurrency === 'coins' ? availableCoins : availableGems;
  };

  const getCurrencyIcon = () => {
    return selectedCurrency === 'coins' ? '🪙' : '💎';
  };

  const handleTrade = () => {
    if (!stock || tradeAmount <= 0) return;
    onTrade(stock, tradeAmount, 'buy', selectedCurrency);
    setTradeAmount(0);
    onClose();
  };

  // Verwende Live-Preise aus dem stockMarket
  const liveStock = stock ? stockMarket.find(s => s.id === stock.id) : null;
  const currentPrice = liveStock?.currentPrice || stock?.currentPrice || 0;
  const changePercent = liveStock?.changePercent || stock?.changePercent || 0;
  const volume = liveStock?.volume || stock?.volume || 0;
  const high24h = liveStock?.high24h || stock?.high24h || 0;
  const low24h = liveStock?.low24h || stock?.low24h || 0;

  const handleQuickAmount = (amount: number) => {
    if (!stock) return;
    
    // Berechne maximale Menge basierend auf verfügbarem Budget
    const maxAmountByBudget = Math.floor(getAvailableCurrency() / currentPrice);
    
    // Verwende die verfügbaren Aktien aus der Hauptfunktion
    const availableShares = getAvailableShares(stock.id);
    
    // Verwende die kleinere der beiden Mengen (Budget oder verfügbare Aktien)
    const maxAmount = Math.min(maxAmountByBudget, availableShares);
    
    // Verwende die gewünschte Menge, aber nicht mehr als verfügbar
    const desiredAmount = Math.min(amount, maxAmount);
    
    // Stelle sicher, dass die Menge nicht negativ ist
    setTradeAmount(Math.max(0, desiredAmount));
  };

  if (!stock) return null;

  return (
    <AnimatePresence key="trading-modal">
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0, y: 50 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0, y: 50 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="bg-gradient-to-br from-gray-900 via-blue-900 to-indigo-900 rounded-2xl border-2 border-blue-500/50 shadow-2xl max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-6 border-b border-blue-500/30 bg-gradient-to-r from-blue-900/50 to-indigo-900/50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{stock.icon}</span>
                  <div>
                    <h3 className="text-lg font-bold text-white">{stock.name}</h3>
                    <div className="text-sm text-gray-400">{stock.symbol} • {stock.sector}</div>
                  </div>
                </div>
                <motion.button
                  onClick={onClose}
                  className="text-gray-400 hover:text-white text-xl transition-colors"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  ✕
                </motion.button>
              </div>
              <div className="mt-3 flex items-center justify-between">
                <div className="text-right">
                  <div className="text-2xl font-bold text-white">{formatNumber(currentPrice)}</div>
                  <div className={`text-sm ${changePercent >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {changePercent >= 0 ? '↗️' : '↘️'} {changePercent.toFixed(2)}%
                  </div>
                </div>
                <div className="text-sm text-gray-400">
                  <div>24h: {formatNumber(low24h)}-{formatNumber(high24h)}</div>
                  <div>Vol: {formatNumber(volume)}</div>
                </div>
              </div>
            </div>

            {/* Trading Content */}
            <div className="p-6 space-y-4">
              {/* Currency Selector */}
              <div className="flex gap-2">
                <button
                  onClick={() => setSelectedCurrency('coins')}
                  className={`flex-1 py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
                    selectedCurrency === 'coins'
                      ? 'bg-yellow-600 text-white'
                      : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                  }`}
                >
                  <span>🪙</span>
                  <div className="flex flex-col items-start">
                    <span className="font-semibold">Münzen</span>
                    <span className="text-xs opacity-75">{formatNumber(availableCoins)}</span>
                  </div>
                </button>
                <button
                  onClick={() => setSelectedCurrency('gems')}
                  className={`flex-1 py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
                    selectedCurrency === 'gems'
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                  }`}
                >
                  <span>💎</span>
                  <div className="flex flex-col items-start">
                    <span className="font-semibold">Diamanten</span>
                    <span className="text-xs opacity-75">{formatNumber(availableGems)}</span>
                  </div>
                </button>
              </div>



              {/* Quick Amount Buttons */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Schnell-Menge
                </label>
                <div className="space-y-2">
                  {/* Erste Reihe - Kleinere Mengen */}
                  <div className="grid grid-cols-4 gap-2">
                    {[250, 500, 1000, 5000].map((amount, index) => (
                      <button
                        key={`buy-small-${amount}-${index}`}
                        onClick={() => handleQuickAmount(amount)}
                        className="py-2 px-3 bg-gray-600 text-gray-300 hover:bg-gray-500 rounded-lg text-sm font-medium transition-colors"
                      >
                        {amount}
                      </button>
                    ))}
                  </div>
                  {/* Zweite Reihe - Höhere Mengen */}
                  <div className="grid grid-cols-4 gap-2">
                    {[10000, 20000, 35000, 50000].map((amount, index) => (
                      <button
                        key={`buy-large-${amount}-${index}`}
                        onClick={() => handleQuickAmount(amount)}
                        className="py-2 px-3 bg-blue-600 text-white hover:bg-blue-500 rounded-lg text-sm font-medium transition-colors"
                      >
                        {amount}
                      </button>
                    ))}
                  </div>
                  {/* Dritte Reihe - Multiplikatoren */}
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => handleQuickAmount(tradeAmount * 2)}
                      className="py-2 px-3 bg-green-600 text-white hover:bg-green-500 rounded-lg text-sm font-medium transition-colors"
                    >
                      ×2
                    </button>
                    <button
                      onClick={() => handleQuickAmount(tradeAmount * 10)}
                      className="py-2 px-3 bg-orange-600 text-white hover:bg-orange-500 rounded-lg text-sm font-medium transition-colors"
                    >
                      ×10
                    </button>
                  </div>
                </div>
                {/* Limit-Anzeige */}
                {stock && (
                  <div className="mt-2 text-xs text-gray-400">
                    {(() => {
                      const maxByBudget = Math.floor(getAvailableCurrency() / currentPrice);
                      const availableShares = getAvailableShares(stock.id);
                      const limitingFactor = maxByBudget < availableShares ? 'Budget' : 'Verfügbare Aktien';
                      return (
                        <span>
                          Limit: {limitingFactor} ({formatNumber(Math.min(maxByBudget, availableShares))} max)
                        </span>
                      );
                    })()}
                  </div>
                )}
              </div>





              {/* Trade Summary */}
              <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-600">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-gray-300">Ausgewählte Menge:</span>
                  <span className="font-bold text-white">
                    {tradeAmount > 0 ? formatNumber(tradeAmount) : '0'} Stück
                  </span>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-gray-300">Gesamtkosten:</span>
                  <span className="font-bold text-white">
                    {formatNumber(currentPrice * tradeAmount)} {getCurrencyIcon()}
                  </span>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-gray-300">Verfügbares Budget:</span>
                  <span className="text-sm text-gray-400">
                    {formatNumber(getAvailableCurrency())} {getCurrencyIcon()}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-300">Verfügbare Aktien:</span>
                  <span className="text-sm text-gray-400">
                    {getAvailableShares(stock.id) === Infinity ? 'Unbegrenzt' : `${formatNumber(getAvailableShares(stock.id))} Stück`}
                  </span>
                </div>
              </div>

              {/* Trade Button */}
              <motion.button
                onClick={handleTrade}
                disabled={tradeAmount <= 0 || (currentPrice * tradeAmount > getAvailableCurrency())}
                className="w-full py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Kaufen
              </motion.button>

              {currentPrice * tradeAmount > getAvailableCurrency() && (
                <div className="text-red-400 text-sm text-center">
                  Nicht genug {selectedCurrency === 'coins' ? 'Münzen' : 'Diamanten'} verfügbar!
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const CompanionStockMarketModal: React.FC<StockMarketModalProps> = ({
  isOpen,
  onClose
}): React.ReactElement | null => {
  const [updateTrigger, setUpdateTrigger] = useState(0);
  const { companions, coins, gems, formatNumber, addCoins, addGems, spendCoins, spendGems, prestige } = useGameStore();
  
  // Börsen-Speicherüberwachung
  const [stockMarketStorageStats, setStockMarketStorageStats] = useState<any>(null);
  
  // Überwache Börsen-Speicher alle 10 Sekunden (nur Monitoring, keine Bereinigung)
  useEffect(() => {
    if (!isOpen) return;
    
    const checkStockMarketStorage = () => {
      const stats = storageManager.getStockMarketStorageStats();
      setStockMarketStorageStats(stats);
      
      // Nur Monitoring - keine automatische Bereinigung
      if (stats.isCritical) {
        console.log(`📊 Börsen-Speicher kritisch: ${stats.percentage.toFixed(1)}%`);
      } else if (stats.isNearLimit) {
        console.log(`📊 Börsen-Speicher wird knapp: ${stats.percentage.toFixed(1)}%`);
      }
    };
    
    // Sofortige Prüfung
    checkStockMarketStorage();
    
    // Regelmäßige Prüfung (nur Monitoring)
    const interval = setInterval(checkStockMarketStorage, 10000);
    
    return () => clearInterval(interval);
  }, [isOpen]);
  
  // Funktion zur Berechnung der Kryptowährungspreise basierend auf Prestige-Level
  const getCryptoPrice = (basePrice: number) => {
    const prestigeLevel = prestige.level;
    const multiplier = Math.pow(10, prestigeLevel); // 10x teurer pro Prestige
    return basePrice * multiplier;
  };

  const [selectedTab, setSelectedTab] = useState<'market' | 'portfolio' | 'history' | 'charts'>('market');
  const [selectedStock, setSelectedStock] = useState<StockItem | null>(null);
  // Verwende GameStore für Portfolio und Trade History
  const portfolio = useGameStore(state => state.getStockPortfolio());
  const tradeHistory = useGameStore(state => state.getStockTradeHistory());
  // Live Market Activity (für virtuellen Trades Ticker)
  const marketActivity = useGameStore(state => state.stockMarket.globalMarket.marketActivity);
  const addStockToPortfolio = useGameStore(state => state.addStockToPortfolio);
  const addTradeToHistory = useGameStore(state => state.addTradeToHistory);
  const removeStockFromPortfolio = useGameStore(state => state.removeStockFromPortfolio);
  // Timeframe-Auswahl entfernt - nur noch ein vereinfachter Chart
  const [selectedSector, setSelectedSector] = useState<string>('all');
  const [selectedCurrency, setSelectedCurrency] = useState<'coins' | 'gems'>('coins');
  const [searchQuery, setSearchQuery] = useState<string>('');
  
  // News-Ticker States
  const [currentNews, setCurrentNews] = useState<string>('');
  const [currentNewsData, setCurrentNewsData] = useState<{
    text: string;
    stockId: string;
    stockName: string;
    stockSymbol: string;
    type: 'positive' | 'negative' | 'sector';
  } | null>(null);
  
  // Neuer State für das Trading Modal
  const [isTradingModalOpen, setIsTradingModalOpen] = useState(false);
  const [tradingStock, setTradingStock] = useState<StockItem | null>(null);
  
  // Neuer State für das Chart Modal
  const [isChartModalOpen, setIsChartModalOpen] = useState(false);
  const [chartStock, setChartStock] = useState<StockItem | null>(null);
  
  // Neuer State für Verkaufsbestätigung
  const [sellConfirmation, setSellConfirmation] = useState<{
    isOpen: boolean;
    stock: StockItem | null;
    position: { shares: number; currency: 'coins' | 'gems' } | null;
    sellAmount: number;
  }>({
    isOpen: false,
    stock: null,
    position: null,
    sellAmount: 0
  });

  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // Fiktive Unternehmen für Münzen und Diamanten
  const [stockMarket, setStockMarket] = useState<StockItem[]>(() => {
    // Standard-Aktien-Daten
    const defaultStocks: StockItem[] = [
    {
      id: 'garden_tech',
      name: 'GartenTech Solutions',
      symbol: 'GTECH',
      currentPrice: 25.50,
      previousPrice: 24.80,
      change: 0.70,
      changePercent: 2.82,
      volume: 50000,
      type: 'coin',
      icon: '🌱',
      priceHistory: [],
      marketCap: 1275000,
      high24h: 26.20,
      low24h: 24.50,
      description: 'Innovative Garten-Technologie und Automatisierung',
      sector: 'Technology'
    },
    {
      id: 'mining_corp',
      name: 'Mining Corporation',
      symbol: 'MINING',
      currentPrice: 150.00,
      previousPrice: 148.00,
      change: 2.00,
      changePercent: 1.35,
      volume: 15000,
      type: 'coin',
      icon: '⛏️',
      priceHistory: [],
      marketCap: 2250000,
      high24h: 152.00,
      low24h: 147.50,
      description: 'Rohstoffabbau und Metallgewinnung',
      sector: 'Materials'
    },
    {
      id: 'crystal_enterprises',
      name: 'Crystal Enterprises',
      symbol: 'CRYST',
      currentPrice: 75.25,
      previousPrice: 73.80,
      change: 1.45,
      changePercent: 1.96,
      volume: 25000,
      type: 'coin',
      icon: '💎',
      priceHistory: [],
      marketCap: 1881250,
      high24h: 76.50,
      low24h: 73.20,
      description: 'Edelsteinverarbeitung und Schmuckherstellung',
      sector: 'Consumer'
    },
    {
      id: 'magic_industries',
      name: 'Magic Industries',
      symbol: 'MAGIC',
      currentPrice: 300.00,
      previousPrice: 295.00,
      change: 5.00,
      changePercent: 1.69,
      volume: 8000,
      type: 'gem',
      icon: '✨',
      priceHistory: [],
      marketCap: 2400000,
      high24h: 305.00,
      low24h: 293.00,
      description: 'Magische Technologien und Zauberartikel',
      sector: 'Technology'
    },
    {
      id: 'dragon_holdings',
      name: 'Dragon Holdings',
      symbol: 'DRAGN',
      currentPrice: 500.00,
      previousPrice: 480.00,
      change: 20.00,
      changePercent: 4.17,
      volume: 5000,
      type: 'gem',
      icon: '🐉',
      priceHistory: [],
      marketCap: 2500000,
      high24h: 520.00,
      low24h: 475.00,
      description: 'Drachenzucht und magische Kreaturen',
      sector: 'Consumer'
    },
    {
      id: 'phoenix_energy',
      name: 'Phoenix Energy',
      symbol: 'PHNX',
      currentPrice: 180.75,
      previousPrice: 178.50,
      change: 2.25,
      changePercent: 1.26,
      volume: 12000,
      type: 'gem',
      icon: '🔥',
      priceHistory: [],
      marketCap: 1807500,
      high24h: 182.00,
      low24h: 177.80,
      description: 'Erneuerbare Energien und Feuerkraft',
      sector: 'Energy'
    },
    {
      id: 'forest_inc',
      name: 'Forest Inc.',
      symbol: 'FORST',
      currentPrice: 45.80,
      previousPrice: 44.90,
      change: 0.90,
      changePercent: 2.00,
      volume: 35000,
      type: 'coin',
      icon: '🌲',
      priceHistory: [],
      marketCap: 1145000,
      high24h: 46.50,
      low24h: 44.60,
      description: 'Nachhaltige Forstwirtschaft und Holzverarbeitung',
      sector: 'Materials'
    },
    {
      id: 'aqua_tech',
      name: 'AquaTech Systems',
      symbol: 'AQUA',
      currentPrice: 95.30,
      previousPrice: 94.20,
      change: 1.10,
      changePercent: 1.17,
      volume: 20000,
      type: 'coin',
      icon: '💧',
      priceHistory: [],
      marketCap: 1906000,
      high24h: 96.00,
      low24h: 93.80,
      description: 'Wasseraufbereitung und Aquakultur',
      sector: 'Utilities'
    },
    // Neue Unternehmen - 20 weitere
    {
      id: 'solar_power',
      name: 'Solar Power Co.',
      symbol: 'SOLAR',
      currentPrice: 65.40,
      previousPrice: 64.20,
      change: 1.20,
      changePercent: 1.87,
      volume: 28000,
      type: 'coin',
      icon: '☀️',
      priceHistory: [],
      marketCap: 1635000,
      high24h: 66.10,
      low24h: 63.90,
      description: 'Solarenergie und Photovoltaik',
      sector: 'Energy'
    },
    {
      id: 'wind_energy',
      name: 'Wind Energy Ltd.',
      symbol: 'WIND',
      currentPrice: 88.90,
      previousPrice: 87.50,
      change: 1.40,
      changePercent: 1.60,
      volume: 22000,
      type: 'coin',
      icon: '💨',
      priceHistory: [],
      marketCap: 2222500,
      high24h: 89.50,
      low24h: 87.20,
      description: 'Windkraft und erneuerbare Energien',
      sector: 'Energy'
    },
    {
      id: 'tech_innovations',
      name: 'Tech Innovations',
      symbol: 'TECH',
      currentPrice: 120.75,
      previousPrice: 118.90,
      change: 1.85,
      changePercent: 1.56,
      volume: 18000,
      type: 'coin',
      icon: '💻',
      priceHistory: [],
      marketCap: 3018750,
      high24h: 121.50,
      low24h: 118.40,
      description: 'Innovative Technologielösungen',
      sector: 'Technology'
    },
    {
      id: 'food_delivery',
      name: 'Food Delivery Express',
      symbol: 'FOOD',
      currentPrice: 35.20,
      previousPrice: 34.80,
      change: 0.40,
      changePercent: 1.15,
      volume: 45000,
      type: 'coin',
      icon: '🍕',
      priceHistory: [],
      marketCap: 880000,
      high24h: 35.80,
      low24h: 34.60,
      description: 'Lieferservice und Gastronomie',
      sector: 'Consumer'
    },
    {
      id: 'fashion_brands',
      name: 'Fashion Brands Group',
      symbol: 'FASH',
      currentPrice: 55.60,
      previousPrice: 54.90,
      change: 0.70,
      changePercent: 1.28,
      volume: 32000,
      type: 'coin',
      icon: '👗',
      priceHistory: [],
      marketCap: 1390000,
      high24h: 56.20,
      low24h: 54.70,
      description: 'Mode und Bekleidungsindustrie',
      sector: 'Consumer'
    },
    {
      id: 'construction_co',
      name: 'Construction Co.',
      symbol: 'BUILD',
      currentPrice: 78.30,
      previousPrice: 77.10,
      change: 1.20,
      changePercent: 1.56,
      volume: 25000,
      type: 'coin',
      icon: '🏗️',
      priceHistory: [],
      marketCap: 1957500,
      high24h: 79.00,
      low24h: 76.90,
      description: 'Bauindustrie und Infrastruktur',
      sector: 'Materials'
    },
    {
      id: 'healthcare_plus',
      name: 'Healthcare Plus',
      symbol: 'HEALTH',
      currentPrice: 145.80,
      previousPrice: 143.90,
      change: 1.90,
      changePercent: 1.32,
      volume: 15000,
      type: 'coin',
      icon: '🏥',
      priceHistory: [],
      marketCap: 3645000,
      high24h: 146.50,
      low24h: 143.60,
      description: 'Gesundheitswesen und Medizin',
      sector: 'Consumer'
    },
    {
      id: 'transport_logistics',
      name: 'Transport & Logistics',
      symbol: 'TRANS',
      currentPrice: 42.50,
      previousPrice: 41.80,
      change: 0.70,
      changePercent: 1.67,
      volume: 38000,
      type: 'coin',
      icon: '🚚',
      priceHistory: [],
      marketCap: 1062500,
      high24h: 43.10,
      low24h: 41.60,
      description: 'Transport und Logistik',
      sector: 'Consumer'
    },
    {
      id: 'entertainment_media',
      name: 'Entertainment Media',
      symbol: 'ENT',
      currentPrice: 95.40,
      previousPrice: 94.20,
      change: 1.20,
      changePercent: 1.27,
      volume: 20000,
      type: 'coin',
      icon: '🎬',
      priceHistory: [],
      marketCap: 2385000,
      high24h: 96.00,
      low24h: 94.00,
      description: 'Unterhaltung und Medien',
      sector: 'Consumer'
    },
    {
      id: 'banking_finance',
      name: 'Banking & Finance',
      symbol: 'BANK',
      currentPrice: 165.30,
      previousPrice: 163.80,
      change: 1.50,
      changePercent: 0.92,
      volume: 12000,
      type: 'coin',
      icon: '🏦',
      priceHistory: [],
      marketCap: 4132500,
      high24h: 166.00,
      low24h: 163.50,
      description: 'Bankwesen und Finanzdienstleistungen',
      sector: 'Consumer'
    },
    {
      id: 'telecom_networks',
      name: 'Telecom Networks',
      symbol: 'TELECOM',
      currentPrice: 88.90,
      previousPrice: 87.60,
      change: 1.30,
      changePercent: 1.48,
      volume: 22000,
      type: 'coin',
      icon: '📱',
      priceHistory: [],
      marketCap: 2222500,
      high24h: 89.50,
      low24h: 87.40,
      description: 'Telekommunikation und Netzwerke',
      sector: 'Technology'
    },
    {
      id: 'pharmaceuticals',
      name: 'Pharmaceuticals Inc.',
      symbol: 'PHARMA',
      currentPrice: 185.60,
      previousPrice: 183.40,
      change: 2.20,
      changePercent: 1.20,
      volume: 10000,
      type: 'coin',
      icon: '💊',
      priceHistory: [],
      marketCap: 4640000,
      high24h: 186.20,
      low24h: 183.20,
      description: 'Pharmazeutische Industrie',
      sector: 'Consumer'
    },
    {
      id: 'real_estate',
      name: 'Real Estate Trust',
      symbol: 'REAL',
      currentPrice: 125.40,
      previousPrice: 124.20,
      change: 1.20,
      changePercent: 0.97,
      volume: 16000,
      type: 'coin',
      icon: '🏠',
      priceHistory: [],
      marketCap: 3135000,
      high24h: 126.00,
      low24h: 124.00,
      description: 'Immobilien und Grundbesitz',
      sector: 'Consumer'
    },
    {
      id: 'agriculture_farm',
      name: 'Agriculture Farm',
      symbol: 'AGRI',
      currentPrice: 38.70,
      previousPrice: 38.10,
      change: 0.60,
      changePercent: 1.57,
      volume: 42000,
      type: 'coin',
      icon: '🌾',
      priceHistory: [],
      marketCap: 967500,
      high24h: 39.20,
      low24h: 37.90,
      description: 'Landwirtschaft und Lebensmittel',
      sector: 'Consumer'
    },
    {
      id: 'tourism_travel',
      name: 'Tourism & Travel',
      symbol: 'TRAVEL',
      currentPrice: 72.80,
      previousPrice: 71.90,
      change: 0.90,
      changePercent: 1.25,
      volume: 28000,
      type: 'coin',
      icon: '✈️',
      priceHistory: [],
      marketCap: 1820000,
      high24h: 73.40,
      low24h: 71.70,
      description: 'Tourismus und Reisebranche',
      sector: 'Consumer'
    },
    {
      id: 'education_learning',
      name: 'Education & Learning',
      symbol: 'EDU',
      currentPrice: 58.90,
      previousPrice: 58.20,
      change: 0.70,
      changePercent: 1.20,
      volume: 30000,
      type: 'coin',
      icon: '📚',
      priceHistory: [],
      marketCap: 1472500,
      high24h: 59.50,
      low24h: 58.00,
      description: 'Bildung und Weiterbildung',
      sector: 'Consumer'
    },
    {
      id: 'sports_fitness',
      name: 'Sports & Fitness',
      symbol: 'SPORT',
      currentPrice: 82.40,
      previousPrice: 81.60,
      change: 0.80,
      changePercent: 0.98,
      volume: 24000,
      type: 'coin',
      icon: '⚽',
      priceHistory: [],
      marketCap: 2060000,
      high24h: 83.00,
      low24h: 81.40,
      description: 'Sport und Fitnessindustrie',
      sector: 'Consumer'
    },
    {
      id: 'automotive_tech',
      name: 'Automotive Tech',
      symbol: 'AUTO',
      currentPrice: 135.60,
      previousPrice: 134.20,
      change: 1.40,
      changePercent: 1.04,
      volume: 14000,
      type: 'coin',
      icon: '🚗',
      priceHistory: [],
      marketCap: 3390000,
      high24h: 136.20,
      low24h: 134.00,
      description: 'Automobilindustrie und Technologie',
      sector: 'Technology'
    },
    {
      id: 'cosmetics_beauty',
      name: 'Cosmetics & Beauty',
      symbol: 'BEAUTY',
      currentPrice: 95.80,
      previousPrice: 94.90,
      change: 0.90,
      changePercent: 0.95,
      volume: 20000,
      type: 'coin',
      icon: '💄',
      priceHistory: [],
      marketCap: 2395000,
      high24h: 96.40,
      low24h: 94.70,
      description: 'Kosmetik und Schönheitspflege',
      sector: 'Consumer'
    },
    {
      id: 'gaming_entertainment',
      name: 'Gaming Entertainment',
      symbol: 'GAME',
      currentPrice: 110.30,
      previousPrice: 109.20,
      change: 1.10,
      changePercent: 1.01,
      volume: 18000,
      type: 'coin',
      icon: '🎮',
      priceHistory: [],
      marketCap: 2757500,
      high24h: 111.00,
      low24h: 109.00,
      description: 'Gaming und interaktive Unterhaltung',
      sector: 'Technology'
    },
    {
      id: 'pet_care',
      name: 'Pet Care Services',
      symbol: 'PET',
      currentPrice: 45.60,
      previousPrice: 44.90,
      change: 0.70,
      changePercent: 1.56,
      volume: 35000,
      type: 'coin',
      icon: '🐕',
      priceHistory: [],
      marketCap: 1140000,
      high24h: 46.20,
      low24h: 44.70,
      description: 'Tierpflege und Haustierbedarf',
      sector: 'Consumer'
    },
    {
      id: 'home_improvement',
      name: 'Home Improvement',
      symbol: 'HOME',
      currentPrice: 68.90,
      previousPrice: 68.20,
      change: 0.70,
      changePercent: 1.03,
      volume: 26000,
      type: 'coin',
      icon: '🔨',
      priceHistory: [],
      marketCap: 1722500,
      high24h: 69.50,
      low24h: 68.00,
      description: 'Heimwerker und Hausverbesserung',
      sector: 'Consumer'
    },
    {
      id: 'jewelry_luxury',
      name: 'Jewelry & Luxury',
      symbol: 'LUXURY',
      currentPrice: 250.00,
      previousPrice: 245.00,
      change: 5.00,
      changePercent: 2.04,
      volume: 8000,
      type: 'gem',
      icon: '💍',
      priceHistory: [],
      marketCap: 2000000,
      high24h: 255.00,
      low24h: 243.00,
      description: 'Schmuck und Luxusgüter',
      sector: 'Consumer'
    },
    {
      id: 'space_exploration',
      name: 'Space Exploration',
      symbol: 'SPACE',
      currentPrice: 400.00,
      previousPrice: 385.00,
      change: 15.00,
      changePercent: 3.90,
      volume: 6000,
      type: 'gem',
      icon: '🚀',
      priceHistory: [],
      marketCap: 2000000,
      high24h: 410.00,
      low24h: 380.00,
      description: 'Weltraumforschung und Technologie',
      sector: 'Technology'
    },
    // Weitere 22 Unternehmen
    {
      id: 'cyber_security',
      name: 'Cyber Security Corp.',
      symbol: 'CYBER',
      currentPrice: 175.40,
      previousPrice: 173.20,
      change: 2.20,
      changePercent: 1.27,
      volume: 11000,
      type: 'coin',
      icon: '🔒',
      priceHistory: [],
      marketCap: 4385000,
      high24h: 176.00,
      low24h: 173.00,
      description: 'Cybersicherheit und Datenschutz',
      sector: 'Technology'
    },
    {
      id: 'artificial_intelligence',
      name: 'Artificial Intelligence Ltd.',
      symbol: 'AI',
      currentPrice: 220.80,
      previousPrice: 218.50,
      change: 2.30,
      changePercent: 1.05,
      volume: 9000,
      type: 'coin',
      icon: '🤖',
      priceHistory: [],
      marketCap: 5520000,
      high24h: 221.50,
      low24h: 218.20,
      description: 'KI-Technologie und Machine Learning',
      sector: 'Technology'
    },
    {
      id: 'blockchain_tech',
      name: 'Blockchain Technology',
      symbol: 'BLOCK',
      currentPrice: 185.60,
      previousPrice: 183.40,
      change: 2.20,
      changePercent: 1.20,
      volume: 10000,
      type: 'coin',
      icon: '⛓️',
      priceHistory: [],
      marketCap: 4640000,
      high24h: 186.20,
      low24h: 183.20,
      description: 'Blockchain und Kryptowährungen',
      sector: 'Technology'
    },
    {
      id: 'cloud_computing',
      name: 'Cloud Computing Solutions',
      symbol: 'CLOUD',
      currentPrice: 155.30,
      previousPrice: 153.80,
      change: 1.50,
      changePercent: 0.97,
      volume: 13000,
      type: 'coin',
      icon: '☁️',
      priceHistory: [],
      marketCap: 3882500,
      high24h: 156.00,
      low24h: 153.60,
      description: 'Cloud-Computing und Datenspeicherung',
      sector: 'Technology'
    },
    {
      id: 'robotics_automation',
      name: 'Robotics & Automation',
      symbol: 'ROBOT',
      currentPrice: 195.70,
      previousPrice: 193.40,
      change: 2.30,
      changePercent: 1.19,
      volume: 9500,
      type: 'coin',
      icon: '🤖',
      priceHistory: [],
      marketCap: 4892500,
      high24h: 196.30,
      low24h: 193.20,
      description: 'Robotik und Automatisierung',
      sector: 'Technology'
    },
    {
      id: 'biotech_research',
      name: 'Biotech Research',
      symbol: 'BIOTECH',
      currentPrice: 165.80,
      previousPrice: 164.20,
      change: 1.60,
      changePercent: 0.97,
      volume: 12000,
      type: 'coin',
      icon: '🧬',
      priceHistory: [],
      marketCap: 4145000,
      high24h: 166.50,
      low24h: 164.00,
      description: 'Biotechnologie und Genforschung',
      sector: 'Technology'
    },
    {
      id: 'quantum_computing',
      name: 'Quantum Computing',
      symbol: 'QUANTUM',
      currentPrice: 350.00,
      previousPrice: 340.00,
      change: 10.00,
      changePercent: 2.94,
      volume: 7000,
      type: 'gem',
      icon: '⚛️',
      priceHistory: [],
      marketCap: 1750000,
      high24h: 355.00,
      low24h: 338.00,
      description: 'Quantencomputer und Quantentechnologie',
      sector: 'Technology'
    },
    {
      id: 'virtual_reality',
      name: 'Virtual Reality Tech',
      symbol: 'VR',
      currentPrice: 125.40,
      previousPrice: 124.20,
      change: 1.20,
      changePercent: 0.97,
      volume: 16000,
      type: 'coin',
      icon: '🥽',
      priceHistory: [],
      marketCap: 3135000,
      high24h: 126.00,
      low24h: 124.00,
      description: 'Virtual Reality und Augmented Reality',
      sector: 'Technology'
    },
    {
      id: 'internet_of_things',
      name: 'Internet of Things',
      symbol: 'IOT',
      currentPrice: 85.60,
      previousPrice: 84.80,
      change: 0.80,
      changePercent: 0.94,
      volume: 24000,
      type: 'coin',
      icon: '📡',
      priceHistory: [],
      marketCap: 2140000,
      high24h: 86.20,
      low24h: 84.60,
      description: 'IoT-Geräte und Smart Home',
      sector: 'Technology'
    },
    {
      id: 'data_analytics',
      name: 'Data Analytics Corp.',
      symbol: 'DATA',
      currentPrice: 145.20,
      previousPrice: 143.80,
      change: 1.40,
      changePercent: 0.97,
      volume: 14000,
      type: 'coin',
      icon: '📊',
      priceHistory: [],
      marketCap: 3630000,
      high24h: 145.80,
      low24h: 143.60,
      description: 'Datenanalyse und Business Intelligence',
      sector: 'Technology'
    },
    {
      id: 'software_development',
      name: 'Software Development',
      symbol: 'SOFT',
      currentPrice: 105.80,
      previousPrice: 104.90,
      change: 0.90,
      changePercent: 0.86,
      volume: 20000,
      type: 'coin',
      icon: '💻',
      priceHistory: [],
      marketCap: 2645000,
      high24h: 106.40,
      low24h: 104.70,
      description: 'Software-Entwicklung und Programmierung',
      sector: 'Technology'
    },
    {
      id: 'mobile_apps',
      name: 'Mobile Apps Inc.',
      symbol: 'MOBILE',
      currentPrice: 75.40,
      previousPrice: 74.80,
      change: 0.60,
      changePercent: 0.80,
      volume: 28000,
      type: 'coin',
      icon: '📱',
      priceHistory: [],
      marketCap: 1885000,
      high24h: 76.00,
      low24h: 74.60,
      description: 'Mobile App-Entwicklung',
      sector: 'Technology'
    },
    {
      id: 'web_services',
      name: 'Web Services Ltd.',
      symbol: 'WEB',
      currentPrice: 65.20,
      previousPrice: 64.60,
      change: 0.60,
      changePercent: 0.93,
      volume: 32000,
      type: 'coin',
      icon: '🌐',
      priceHistory: [],
      marketCap: 1630000,
      high24h: 65.80,
      low24h: 64.40,
      description: 'Web-Dienste und Hosting',
      sector: 'Technology'
    },
    {
      id: 'digital_marketing',
      name: 'Digital Marketing',
      symbol: 'DIGITAL',
      currentPrice: 55.80,
      previousPrice: 55.20,
      change: 0.60,
      changePercent: 1.09,
      volume: 35000,
      type: 'coin',
      icon: '📈',
      priceHistory: [],
      marketCap: 1395000,
      high24h: 56.40,
      low24h: 55.00,
      description: 'Digitales Marketing und Werbung',
      sector: 'Consumer'
    },
    {
      id: 'ecommerce_platform',
      name: 'E-Commerce Platform',
      symbol: 'ECOMM',
      currentPrice: 95.60,
      previousPrice: 94.80,
      change: 0.80,
      changePercent: 0.84,
      volume: 22000,
      type: 'coin',
      icon: '🛒',
      priceHistory: [],
      marketCap: 2390000,
      high24h: 96.20,
      low24h: 94.60,
      description: 'Online-Handel und E-Commerce',
      sector: 'Consumer'
    },
    {
      id: 'social_media',
      name: 'Social Media Network',
      symbol: 'SOCIAL',
      currentPrice: 115.40,
      previousPrice: 114.60,
      change: 0.80,
      changePercent: 0.70,
      volume: 18000,
      type: 'coin',
      icon: '📱',
      priceHistory: [],
      marketCap: 2885000,
      high24h: 116.00,
      low24h: 114.40,
      description: 'Soziale Medien und Netzwerke',
      sector: 'Technology'
    },
    {
      id: 'streaming_services',
      name: 'Streaming Services',
      symbol: 'STREAM',
      currentPrice: 85.20,
      previousPrice: 84.60,
      change: 0.60,
      changePercent: 0.71,
      volume: 25000,
      type: 'coin',
      icon: '📺',
      priceHistory: [],
      marketCap: 2130000,
      high24h: 85.80,
      low24h: 84.40,
      description: 'Video-Streaming und Unterhaltung',
      sector: 'Consumer'
    },
    {
      id: 'music_industry',
      name: 'Music Industry Corp.',
      symbol: 'MUSIC',
      currentPrice: 72.80,
      previousPrice: 72.20,
      change: 0.60,
      changePercent: 0.83,
      volume: 30000,
      type: 'coin',
      icon: '🎵',
      priceHistory: [],
      marketCap: 1820000,
      high24h: 73.40,
      low24h: 72.00,
      description: 'Musikindustrie und Audio-Streaming',
      sector: 'Consumer'
    },
    {
      id: 'publishing_media',
      name: 'Publishing & Media',
      symbol: 'PUBLISH',
      currentPrice: 68.40,
      previousPrice: 67.80,
      change: 0.60,
      changePercent: 0.88,
      volume: 32000,
      type: 'coin',
      icon: '📰',
      priceHistory: [],
      marketCap: 1710000,
      high24h: 69.00,
      low24h: 67.60,
      description: 'Verlagswesen und Medien',
      sector: 'Consumer'
    },
    {
      id: 'advertising_agency',
      name: 'Advertising Agency',
      symbol: 'ADVERT',
      currentPrice: 78.60,
      previousPrice: 78.00,
      change: 0.60,
      changePercent: 0.77,
      volume: 26000,
      type: 'coin',
      icon: '📢',
      priceHistory: [],
      marketCap: 1965000,
      high24h: 79.20,
      low24h: 77.80,
      description: 'Werbung und Marketing-Agentur',
      sector: 'Consumer'
    },
    {
      id: 'consulting_services',
      name: 'Consulting Services',
      symbol: 'CONSULT',
      currentPrice: 125.80,
      previousPrice: 125.20,
      change: 0.60,
      changePercent: 0.48,
      volume: 16000,
      type: 'coin',
      icon: '💼',
      priceHistory: [],
      marketCap: 3145000,
      high24h: 126.40,
      low24h: 125.00,
      description: 'Unternehmensberatung und Services',
      sector: 'Consumer'
    },
    {
      id: 'legal_services',
      name: 'Legal Services',
      symbol: 'LEGAL',
      currentPrice: 145.60,
      previousPrice: 145.00,
      change: 0.60,
      changePercent: 0.41,
      volume: 12000,
      type: 'coin',
      icon: '⚖️',
      priceHistory: [],
      marketCap: 3640000,
      high24h: 146.20,
      low24h: 144.80,
      description: 'Rechtsdienstleistungen und Beratung',
      sector: 'Consumer'
    },
    {
      id: 'accounting_finance',
      name: 'Accounting & Finance',
      symbol: 'ACCOUNT',
      currentPrice: 135.40,
      previousPrice: 134.80,
      change: 0.60,
      changePercent: 0.45,
      volume: 14000,
      type: 'coin',
      icon: '📋',
      priceHistory: [],
      marketCap: 3385000,
      high24h: 136.00,
      low24h: 134.60,
      description: 'Buchhaltung und Finanzberatung',
      sector: 'Consumer'
    },
    // Kryptowährungen - Preise basieren auf Prestige-Level
    {
      id: 'vibtc',
      name: 'VibTC',
      symbol: 'VIBTC',
      currentPrice: getCryptoPrice(0.85), // 85 Cents Basispreis
      previousPrice: getCryptoPrice(0.84),
      change: getCryptoPrice(0.01),
      changePercent: 1.19,
      volume: 500000,
      type: 'coin',
      icon: '₿',
      priceHistory: [],
      marketCap: getCryptoPrice(0.85) * 500000,
      high24h: getCryptoPrice(0.87),
      low24h: getCryptoPrice(0.83),
      description: 'VibeGarden Token Coin - Native Kryptowährung',
      sector: 'Technology'
    },
    {
      id: 'vibeth',
      name: 'VibeTH',
      symbol: 'VIBETH',
      currentPrice: getCryptoPrice(1.25), // 1.25 Cents Basispreis
      previousPrice: getCryptoPrice(1.24),
      change: getCryptoPrice(0.01),
      changePercent: 0.81,
      volume: 350000,
      type: 'coin',
      icon: 'Ξ',
      priceHistory: [],
      marketCap: getCryptoPrice(1.25) * 350000,
      high24h: getCryptoPrice(1.27),
      low24h: getCryptoPrice(1.23),
      description: 'VibeGarden Token Hub - Smart Contract Platform',
      sector: 'Technology'
    },
    {
      id: 'vibedoge',
      name: 'VibeDoge',
      symbol: 'VIBEDOGE',
      currentPrice: getCryptoPrice(0.00042), // 0.00042 Cents Basispreis (sehr niedrig)
      previousPrice: getCryptoPrice(0.00041),
      change: getCryptoPrice(0.00001),
      changePercent: 2.44,
      volume: 2500000, // Sehr hohes Volumen für Volatilität
      type: 'coin',
      icon: '🐕',
      priceHistory: [],
      marketCap: getCryptoPrice(0.00042) * 2500000,
      high24h: getCryptoPrice(0.00050), // Sehr hohe 24h High
      low24h: getCryptoPrice(0.00035), // Sehr niedrige 24h Low
      description: 'VibeGarden Doge Token - Sehr volatile Meme-Kryptowährung',
      sector: 'Technology'
    },
    // 20 Hoch volatile Aktien
    {
      id: 'meme_coin_express',
      name: 'Meme Coin Express',
      symbol: 'MEME',
      currentPrice: 0.0085,
      previousPrice: 0.0072,
      change: 0.0013,
      changePercent: 18.06,
      volume: 2500000,
      type: 'coin',
      icon: '🚀',
      priceHistory: [],
      marketCap: 21250,
      high24h: 0.0095,
      low24h: 0.0068,
      description: 'Ultra-volatile Meme-Kryptowährung mit extremen Schwankungen',
      sector: 'Technology'
    },
    {
      id: 'crypto_pump',
      name: 'Crypto Pump & Dump',
      symbol: 'PUMP',
      currentPrice: 0.0234,
      previousPrice: 0.0198,
      change: 0.0036,
      changePercent: 18.18,
      volume: 1800000,
      type: 'coin',
      icon: '📈',
      priceHistory: [],
      marketCap: 58500,
      high24h: 0.0250,
      low24h: 0.0185,
      description: 'Hoch spekulative Kryptowährung mit wilden Preisschwankungen',
      sector: 'Technology'
    },
    {
      id: 'volatile_tech',
      name: 'Volatile Tech Solutions',
      symbol: 'VOLT',
      currentPrice: 12.45,
      previousPrice: 10.80,
      change: 1.65,
      changePercent: 15.28,
      volume: 85000,
      type: 'coin',
      icon: '⚡',
      priceHistory: [],
      marketCap: 311250,
      high24h: 13.20,
      low24h: 10.50,
      description: 'Technologie-Startup mit extrem volatilen Aktienkursen',
      sector: 'Technology'
    },
    {
      id: 'biotech_boom',
      name: 'Biotech Boom Corp',
      symbol: 'BIOT',
      currentPrice: 8.75,
      previousPrice: 7.60,
      change: 1.15,
      changePercent: 15.13,
      volume: 120000,
      type: 'coin',
      icon: '🧬',
      priceHistory: [],
      marketCap: 218750,
      high24h: 9.10,
      low24h: 7.40,
      description: 'Biotechnologie-Unternehmen mit hoher Volatilität',
      sector: 'Consumer'
    },
    {
      id: 'crypto_gambling',
      name: 'Crypto Gambling Inc',
      symbol: 'GAMBL',
      currentPrice: 0.0456,
      previousPrice: 0.0398,
      change: 0.0058,
      changePercent: 14.57,
      volume: 950000,
      type: 'coin',
      icon: '🎰',
      priceHistory: [],
      marketCap: 114000,
      high24h: 0.0480,
      low24h: 0.0385,
      description: 'Krypto-Glücksspiel-Plattform mit wilden Kursschwankungen',
      sector: 'Consumer'
    },
    {
      id: 'ai_hype',
      name: 'AI Hype Machine',
      symbol: 'AIHYP',
      currentPrice: 25.80,
      previousPrice: 22.50,
      change: 3.30,
      changePercent: 14.67,
      volume: 65000,
      type: 'coin',
      icon: '🤖',
      priceHistory: [],
      marketCap: 645000,
      high24h: 27.20,
      low24h: 22.10,
      description: 'KI-Startup mit überhitzten Erwartungen und hoher Volatilität',
      sector: 'Technology'
    },
    {
      id: 'crypto_mining',
      name: 'Crypto Mining Rush',
      symbol: 'MINER',
      currentPrice: 0.0123,
      previousPrice: 0.0108,
      change: 0.0015,
      changePercent: 13.89,
      volume: 1500000,
      type: 'coin',
      icon: '⛏️',
      priceHistory: [],
      marketCap: 30750,
      high24h: 0.0130,
      low24h: 0.0105,
      description: 'Krypto-Mining-Unternehmen mit volatilen Erträgen',
      sector: 'Technology'
    },
    {
      id: 'defi_protocol',
      name: 'DeFi Protocol Token',
      symbol: 'DEFI',
      currentPrice: 0.0890,
      previousPrice: 0.0785,
      change: 0.0105,
      changePercent: 13.38,
      volume: 750000,
      type: 'coin',
      icon: '🔗',
      priceHistory: [],
      marketCap: 222500,
      high24h: 0.0920,
      low24h: 0.0770,
      description: 'Dezentrales Finanzprotokoll mit hoher Volatilität',
      sector: 'Technology'
    },
    {
      id: 'nft_marketplace',
      name: 'NFT Marketplace Pro',
      symbol: 'NFTMP',
      currentPrice: 0.0678,
      previousPrice: 0.0600,
      change: 0.0078,
      changePercent: 13.00,
      volume: 850000,
      type: 'coin',
      icon: '🎨',
      priceHistory: [],
      marketCap: 169500,
      high24h: 0.0700,
      low24h: 0.0590,
      description: 'NFT-Marktplatz mit volatilen Handelsvolumina',
      sector: 'Consumer'
    },
    {
      id: 'crypto_gaming',
      name: 'Crypto Gaming Studio',
      symbol: 'GAME',
      currentPrice: 0.0345,
      previousPrice: 0.0306,
      change: 0.0039,
      changePercent: 12.75,
      volume: 1200000,
      type: 'coin',
      icon: '🎮',
      priceHistory: [],
      marketCap: 86250,
      high24h: 0.0360,
      low24h: 0.0300,
      description: 'Krypto-Gaming-Studio mit volatilen Spielertoken',
      sector: 'Consumer'
    },
    {
      id: 'metaverse_land',
      name: 'Metaverse Land Corp',
      symbol: 'META',
      currentPrice: 0.0234,
      previousPrice: 0.0208,
      change: 0.0026,
      changePercent: 12.50,
      volume: 1800000,
      type: 'coin',
      icon: '🌐',
      priceHistory: [],
      marketCap: 58500,
      high24h: 0.0245,
      low24h: 0.0200,
      description: 'Metaverse-Grundstückshandel mit wilden Preisschwankungen',
      sector: 'Technology'
    },
    {
      id: 'crypto_social',
      name: 'Crypto Social Network',
      symbol: 'SOCIAL',
      currentPrice: 0.0156,
      previousPrice: 0.0139,
      change: 0.0017,
      changePercent: 12.23,
      volume: 2500000,
      type: 'coin',
      icon: '📱',
      priceHistory: [],
      marketCap: 39000,
      high24h: 0.0165,
      low24h: 0.0135,
      description: 'Soziales Netzwerk auf Blockchain mit volatilen Token',
      sector: 'Technology'
    },
    {
      id: 'crypto_predictions',
      name: 'Crypto Predictions Market',
      symbol: 'PRED',
      currentPrice: 0.0089,
      previousPrice: 0.0079,
      change: 0.0010,
      changePercent: 12.66,
      volume: 3000000,
      type: 'coin',
      icon: '🔮',
      priceHistory: [],
      marketCap: 22250,
      high24h: 0.0095,
      low24h: 0.0075,
      description: 'Vorhersage-Markt für Krypto-Ereignisse mit hoher Volatilität',
      sector: 'Technology'
    },
    {
      id: 'crypto_lending',
      name: 'Crypto Lending Platform',
      symbol: 'LEND',
      currentPrice: 0.0456,
      previousPrice: 0.0408,
      change: 0.0048,
      changePercent: 11.76,
      volume: 950000,
      type: 'coin',
      icon: '💰',
      priceHistory: [],
      marketCap: 114000,
      high24h: 0.0475,
      low24h: 0.0400,
      description: 'Krypto-Kreditplattform mit volatilen Zinssätzen',
      sector: 'Technology'
    },
    {
      id: 'crypto_insurance',
      name: 'Crypto Insurance Co',
      symbol: 'INSUR',
      currentPrice: 0.0234,
      previousPrice: 0.0210,
      change: 0.0024,
      changePercent: 11.43,
      volume: 1500000,
      type: 'coin',
      icon: '🛡️',
      priceHistory: [],
      marketCap: 58500,
      high24h: 0.0245,
      low24h: 0.0205,
      description: 'Krypto-Versicherungsunternehmen mit volatilen Prämien',
      sector: 'Consumer'
    },
    {
      id: 'crypto_derivatives',
      name: 'Crypto Derivatives Exchange',
      symbol: 'DERIV',
      currentPrice: 0.0789,
      previousPrice: 0.0708,
      change: 0.0081,
      changePercent: 11.44,
      volume: 650000,
      type: 'coin',
      icon: '📊',
      priceHistory: [],
      marketCap: 197250,
      high24h: 0.0820,
      low24h: 0.0700,
      description: 'Derivate-Börse für Kryptowährungen mit hoher Hebelwirkung',
      sector: 'Technology'
    },
    {
      id: 'crypto_staking',
      name: 'Crypto Staking Pool',
      symbol: 'STAKE',
      currentPrice: 0.0123,
      previousPrice: 0.0111,
      change: 0.0012,
      changePercent: 10.81,
      volume: 2000000,
      type: 'coin',
      icon: '🔒',
      priceHistory: [],
      marketCap: 30750,
      high24h: 0.0130,
      low24h: 0.0110,
      description: 'Staking-Pool mit volatilen Renditen',
      sector: 'Technology'
    },
    {
      id: 'crypto_yield',
      name: 'Crypto Yield Farming',
      symbol: 'YIELD',
      currentPrice: 0.0345,
      previousPrice: 0.0312,
      change: 0.0033,
      changePercent: 10.58,
      volume: 1200000,
      type: 'coin',
      icon: '🌾',
      priceHistory: [],
      marketCap: 86250,
      high24h: 0.0360,
      low24h: 0.0310,
      description: 'Yield-Farming-Protokoll mit volatilen APY-Raten',
      sector: 'Technology'
    },
    {
      id: 'crypto_liquidity',
      name: 'Crypto Liquidity Pool',
      symbol: 'LIQUID',
      currentPrice: 0.0567,
      previousPrice: 0.0513,
      change: 0.0054,
      changePercent: 10.53,
      volume: 850000,
      type: 'coin',
      icon: '💧',
      priceHistory: [],
      marketCap: 141750,
      high24h: 0.0590,
      low24h: 0.0510,
      description: 'Liquiditäts-Pool mit volatilen Gebühren',
      sector: 'Technology'
    },
    {
      id: 'crypto_oracle',
      name: 'Crypto Oracle Network',
      symbol: 'ORACL',
      currentPrice: 0.0890,
      previousPrice: 0.0805,
      change: 0.0085,
      changePercent: 10.56,
      volume: 750000,
      type: 'coin',
      icon: '🔮',
      priceHistory: [],
      marketCap: 222500,
      high24h: 0.0920,
      low24h: 0.0800,
      description: 'Oracle-Netzwerk für Blockchain-Daten mit volatilen Preisen',
      sector: 'Technology'
    },
    {
      id: 'auto_graber_xdustries',
      name: 'Auto Gräber Xdustries',
      symbol: 'AGXD',
      currentPrice: 5000.00, // Sehr teuer
      previousPrice: 4950.00,
      change: 50.00,
      changePercent: 1.01,
      volume: 500, // Niedriges Volumen wegen hohem Preis
      type: 'coin',
      icon: '🏭',
      priceHistory: [],
      marketCap: 5000.00 * 500,
      high24h: 5100.00,
      low24h: 4900.00,
      description: 'Premium Auto-Gräber Technologie - Jeder Anteil gibt +1 Münze pro Sekunde',
      sector: 'Technology'
    }
  ];

    // Lade gespeicherte Aktienpreise aus dem GameStore
    const savedStockPrices = useGameStore.getState().getAllStockPrices();
    
    // Wenn gespeicherte Preise vorhanden sind, verwende sie
    if (Object.keys(savedStockPrices).length > 0) {
      return defaultStocks.map(stock => {
        const savedPrice = savedStockPrices[stock.id];
        if (savedPrice) {
          return {
            ...stock,
            currentPrice: savedPrice.currentPrice,
            previousPrice: savedPrice.previousPrice,
            change: savedPrice.change,
            changePercent: savedPrice.changePercent,
            volume: savedPrice.volume,
            high24h: savedPrice.high24h,
            low24h: savedPrice.low24h,
            marketCap: savedPrice.marketCap,
            priceHistory: savedPrice.priceHistory
          };
        }
        return stock;
      });
    }
    
    return defaultStocks;
  });

  // News-Ticker System
  const generateNews = () => {
    const newsTemplates = [
      // Positive Nachrichten
      { text: '📈 {stock} meldet Rekordgewinne - Aktie explodiert!', effect: 0.15, type: 'positive' },
      { text: '🚀 {stock} kündigt revolutionäre Partnerschaft an', effect: 0.12, type: 'positive' },
      { text: '💎 {stock} erhält Patent für bahnbrechende Technologie', effect: 0.10, type: 'positive' },
      { text: '🌟 {stock} übertrifft Analystenerwartungen deutlich', effect: 0.08, type: 'positive' },
      { text: '🔥 {stock} expandiert in neue Märkte', effect: 0.06, type: 'positive' },
      
      // Negative Nachrichten
      { text: '📉 {stock} verpasst Quartalsziele - Aktie stürzt ab!', effect: -0.18, type: 'negative' },
      { text: '💥 {stock} von Regulierungsbehörden untersucht', effect: -0.15, type: 'negative' },
      { text: '⚠️ {stock} warnt vor schwächeren Umsätzen', effect: -0.12, type: 'negative' },
      { text: '🔴 {stock} CEO tritt überraschend zurück', effect: -0.10, type: 'negative' },
      { text: '📉 {stock} verliert wichtigen Kunden', effect: -0.08, type: 'negative' },
      
      // Krypto-spezifische Nachrichten
      { text: '🌙 {stock} wird von berühmtem Tech-Influencer getweetet!', effect: 0.25, type: 'positive' },
      { text: '⚡ {stock} wird von großer Krypto-Börse gelistet', effect: 0.20, type: 'positive' },
      { text: '🔗 {stock} DeFi-Protokoll wird gehackt!', effect: -0.30, type: 'negative' },
      { text: '📱 {stock} App erreicht 1 Million Downloads', effect: 0.08, type: 'positive' },
      { text: '🎮 {stock} Gaming-Token explodiert durch neue Partnerschaft', effect: 0.15, type: 'positive' },
      
      // Markt-Nachrichten
      { text: '📊 Tech-Sektor erholt sich - Alle Tech-Aktien steigen', effect: 0.05, type: 'sector' },
      { text: '💎 Edelstein-Markt unter Druck - Diamanten fallen', effect: -0.03, type: 'sector' },
      { text: '🌱 Garten-Sektor boomt - Grüne Aktien im Aufwind', effect: 0.04, type: 'sector' },
      { text: '⚡ Energie-Sektor schwächelt - Erneuerbare fallen', effect: -0.04, type: 'sector' },
      
      // Zufällige Events
      { text: '🎰 {stock} startet Glücksspiel-Plattform', effect: 0.12, type: 'positive' },
      { text: '🧬 {stock} Biotech-Durchbruch angekündigt', effect: 0.18, type: 'positive' },
      { text: '🤖 {stock} KI-Revolution beginnt', effect: 0.20, type: 'positive' },
      { text: '🌐 {stock} Metaverse-Expansion geplant', effect: 0.14, type: 'positive' },
      { text: '💧 {stock} Wasserknappheit beeinträchtigt Geschäft', effect: -0.12, type: 'negative' },
      { text: '🔥 {stock} Server-Ausfall kostet Millionen', effect: -0.15, type: 'negative' },
      { text: '📱 {stock} App-Store-Sperre droht', effect: -0.10, type: 'negative' },
      { text: '🌪️ {stock} Naturkatastrophe zerstört Rechenzentrum', effect: -0.20, type: 'negative' }
    ];

    const randomStock = stockMarket[Math.floor(Math.random() * stockMarket.length)];
    const randomNews = newsTemplates[Math.floor(Math.random() * newsTemplates.length)];
    
    const newsText = randomNews.text.replace('{stock}', `${randomStock.symbol} (${randomStock.name})`);
    
    return {
      text: newsText,
      effect: randomNews.effect,
      type: randomNews.type,
      stockId: randomStock.id,
      stockName: randomStock.name,
      stockSymbol: randomStock.symbol,
      sector: randomStock.sector
    };
  };

  const applyNewsEffect = (news: any) => {
    setStockMarket(prevMarket => {
      return prevMarket.map(stock => {
        let priceChange = 0;
        
        if (news.type === 'sector') {
          // Sektor-weite Effekte
          if (news.text.includes('Tech-Sektor') && stock.sector === 'Technology') {
            priceChange = news.effect;
          } else if (news.text.includes('Edelstein-Markt') && stock.type === 'gem') {
            priceChange = news.effect;
          } else if (news.text.includes('Garten-Sektor') && stock.sector === 'Materials') {
            priceChange = news.effect;
          } else if (news.text.includes('Energie-Sektor') && stock.sector === 'Energy') {
            priceChange = news.effect;
          }
        } else if (stock.id === news.stockId) {
          // Spezifische Aktien-Effekte
          priceChange = news.effect;
        }
        
        if (priceChange !== 0) {
          const newPrice = stock.currentPrice * (1 + priceChange);
          const priceDiff = newPrice - stock.currentPrice;
          const changePercent = (priceDiff / stock.currentPrice) * 100;
          
          // Trigger verstärkte virtuelle Trading-Aktivität basierend auf News
          if (Math.abs(priceChange) > 0.05) { // Bei signifikanten News
            setTimeout(() => {
              // Erhöhe die Anzahl der virtuellen Trades für diese Aktie
              triggerNewsBasedTrading(stock.id, priceChange > 0 ? 'positive' : 'negative');
            }, 1000); // 1 Sekunde Verzögerung für realistische Reaktion
          }
          
          return {
            ...stock,
            previousPrice: stock.currentPrice,
            currentPrice: Math.max(0.01, newPrice),
            change: priceDiff,
            changePercent: changePercent,
            high24h: Math.max(stock.high24h, newPrice),
            low24h: Math.min(stock.low24h, newPrice),
            marketCap: newPrice * stock.volume
          };
        }
        
        return stock;
      });
    });
  };

  // Funktion für News-basiertes Trading der virtuellen Spieler
  const triggerNewsBasedTrading = (stockId: string, newsType: 'positive' | 'negative') => {
    const state = useGameStore.getState();
    const virtualPlayers = state.stockMarket.globalMarket.virtualPlayers;
    
    if (!virtualPlayers || virtualPlayers.length === 0) return;
    
    // Wähle 20-50 virtuelle Spieler für News-basiertes Trading
    const numberOfNewsTraders = Math.floor(Math.random() * 31) + 20;
    
    for (let i = 0; i < numberOfNewsTraders; i++) {
      const randomPlayer = virtualPlayers[Math.floor(Math.random() * virtualPlayers.length)];
      if (!randomPlayer) continue;
      
      // Bestimme Trade-Typ basierend auf News
      const tradeType = newsType === 'positive' ? 'buy' : 'sell';
      
      // Erhöhe Trade-Größe für News-basiertes Trading
      const baseTradeAmount = Math.floor(Math.random() * 100) + 50; // 50-150 Anteile
      const newsMultiplier = 1.5 + Math.random() * 1.0; // 1.5x bis 2.5x größer
      const tradeAmount = Math.floor(baseTradeAmount * newsMultiplier);
      
      // Führe News-basierten Trade durch
      const currency = Math.random() > 0.5 ? 'coins' : 'gems';
      
      // Debug-Ausgabe für News-basiertes Trading
      console.log(`📰 News-Trade: ${randomPlayer.name} ${tradeType} ${tradeAmount} ${currency} von ${stockId} (${newsType} News)`);
      
      // Aktualisiere Portfolio und globale Anteile (vereinfachte Version)
      const currentTotal = state.stockMarket.globalMarket.totalSharesOwned[stockId] || 0;
      const newTotal = tradeType === 'buy' ? currentTotal + tradeAmount : Math.max(0, currentTotal - tradeAmount);
      
      useGameStore.setState((state) => ({
        stockMarket: {
          ...state.stockMarket,
          globalMarket: {
            ...state.stockMarket.globalMarket,
            totalSharesOwned: {
              ...state.stockMarket.globalMarket.totalSharesOwned,
              [stockId]: newTotal
            },
            marketActivity: [...state.stockMarket.globalMarket.marketActivity, {
              id: `news_activity_${Date.now()}_${Math.random()}`,
              stockId,
              type: tradeType as 'buy' | 'sell',
              amount: tradeAmount,
              price: 0,
              timestamp: new Date().toISOString(),
              currency,
              playerType: 'virtual' as const
            }]
          }
        }
      }));
    }
  };

  // News-Ticker Timer - Kontinuierlicher Durchlauf
  useEffect(() => {
    if (!isOpen) return;
    
    let isActive = true;
    
    // Sofort erste News anzeigen
    const showFirstNews = () => {
      if (!isActive) return;
      const news = generateNews();
      setCurrentNews(news.text);
      setCurrentNewsData({
        text: news.text,
        stockId: news.stockId,
        stockName: news.stockName,
        stockSymbol: news.stockSymbol,
        type: news.type as 'positive' | 'negative' | 'sector'
      });
      applyNewsEffect(news);
    };
    
    // Erste News sofort anzeigen
    showFirstNews();
    
    const newsInterval = setInterval(() => {
      if (!isActive) return;
      
      // Generiere zufällige News mit Marktbeeinflussung
      const news = generateNews();
      setCurrentNews(news.text);
      setCurrentNewsData({
        text: news.text,
        stockId: news.stockId,
        stockName: news.stockName,
        stockSymbol: news.stockSymbol,
        type: news.type as 'positive' | 'negative' | 'sector'
      });
      applyNewsEffect(news);
      

      
      // News nach 15 Sekunden ausblenden
      setTimeout(() => {
        if (isActive) {
          setCurrentNews('');
          setCurrentNewsData(null);
        }
      }, 15000);
      
    }, 30000); // Alle 30 Sekunden neue News
    
    return () => {
      isActive = false;
      clearInterval(newsInterval);
    };
  }, [isOpen]); // Nur isOpen als Dependency

  // Erweitere Preisverlauf für alle Aktien
  useEffect(() => {
    setStockMarket(prev => prev.map(stock => ({
      ...stock,
      priceHistory: generatePriceHistory(stock.currentPrice, stock.type, stock.id)
    })));
  }, []);

  // Timer für kontinuierliche Preisaktualisierungen (auch wenn Börse geschlossen ist)
  useEffect(() => {
    const updateStockPrices = () => {
      setStockMarket(prev => prev.map(stock => {
        let volatility;
        let maxChangePercent;
        
        // Spezielle Volatilität für VibeDoge
        if (stock.id === 'vibedoge') {
          volatility = 0.25; // Sehr hohe Volatilität
          maxChangePercent = 0.15; // Bis zu 15% Änderung pro Update
        } else {
          volatility = stock.type === 'gem' ? 0.12 : 0.08;
          maxChangePercent = 0.02; // Maximal 2% Änderung für normale Aktien
        }
        
        const maxChange = stock.currentPrice * volatility * maxChangePercent;
        const change = (Math.random() - 0.5) * maxChange;
        const newPrice = Math.max(0.00001, stock.currentPrice + change); // Mindestpreis für Kryptos
        const priceChange = newPrice - stock.currentPrice;
        const changePercent = (priceChange / stock.currentPrice) * 100;
        
        // Aktualisiere high24h und low24h
        const newHigh24h = Math.max(stock.high24h || 0, newPrice);
        const newLow24h = Math.min(stock.low24h || newPrice, newPrice);
        
        return {
          ...stock,
          previousPrice: stock.currentPrice,
          currentPrice: newPrice,
          change: priceChange,
          changePercent: changePercent,
          high24h: newHigh24h,
          low24h: newLow24h,
          marketCap: newPrice * stock.volume
        };
      }));
    };

    // Aktualisiere Preise jede Sekunde (frischere Daten für Chart)
    const interval = setInterval(updateStockPrices, 1000);
    
    // Cleanup beim Unmount
    return () => clearInterval(interval);
  }, []); // Leeres Dependency Array - läuft kontinuierlich

  const generatePriceHistory = (currentPrice: number, type: string, stockId?: string) => {
    const history = [];
    let volatility;
    
    // Spezielle Volatilität für VibeDoge
    if (stockId === 'vibedoge') {
      volatility = 0.35; // Extrem hohe Volatilität für Preisverlauf
    } else {
      volatility = type === 'gem' ? 0.12 : 0.08;
    }
    
    let price = currentPrice;
    
    // Optimiert für 1MB Limit: Reduzierte Datenpunkte
    const now = Date.now();
    
    // Für die letzten 7 Tage: Tägliche Datenpunkte (7 Punkte)
    for (let i = 0; i < 7; i++) {
      const change = (Math.random() - 0.5) * volatility * price * 0.8;
      price = Math.max(0.01, price + change);
      history.push({
        timestamp: now - (7 - i) * 24 * 60 * 60 * 1000, // Tägliche Intervalle
        price: price
      });
    }
    
    // Für die letzten 24 Stunden: Stündliche Datenpunkte (24 Punkte)
    for (let i = 0; i < 24; i++) {
      const change = (Math.random() - 0.5) * volatility * price * 0.6;
      price = Math.max(0.01, price + change);
      history.push({
        timestamp: now - (24 - i) * 60 * 60 * 1000, // Stündliche Intervalle
        price: price
      });
    }
    
    // Für die letzten 6 Stunden: 10-Minuten-Intervalle (36 Punkte)
    for (let i = 0; i < 36; i++) {
      const change = (Math.random() - 0.5) * volatility * price;
      price = Math.max(0.01, price + change);
      history.push({
        timestamp: now - (36 - i) * 10 * 60 * 1000, // 10-Minuten-Intervalle
        price: price
      });
    }
    
    // Für die letzten 60 Minuten: 5-Minuten-Intervalle (12 Punkte)
    for (let i = 0; i < 12; i++) {
      const change = (Math.random() - 0.5) * volatility * price * 0.8;
      price = Math.max(0.01, price + change);
      history.push({
        timestamp: now - (12 - i) * 5 * 60 * 1000, // 5-Minuten-Intervalle
        price: price
      });
    }
    
    // Gesamt: 79 Datenpunkte statt über 3000 (massive Reduzierung für 1MB Limit)
    return history;
  };

  // Hilfsfunktion zum Speichern der Aktienpreise im GameStore (optimiert für 1MB Limit)
  const saveStockPricesToStore = (stocks: StockItem[]) => {
    const stockData = stocks.map(stock => ({
      id: stock.id,
      currentPrice: stock.currentPrice,
      previousPrice: stock.previousPrice,
      change: stock.change,
      changePercent: stock.changePercent,
      volume: stock.volume,
      high24h: stock.high24h,
      low24h: stock.low24h,
      marketCap: stock.marketCap,
      // Reduziere priceHistory auf maximal 50 Einträge für 1MB Limit
      priceHistory: stock.priceHistory.slice(-50)
    }));
    
    useGameStore.getState().initializeStockPrices(stockData);
  };

  // Hilfsfunktion zum Laden der Aktienpreise aus dem GameStore
  const loadStockPricesFromStore = (defaultStocks: StockItem[]): StockItem[] => {
    const savedStockPrices = useGameStore.getState().getAllStockPrices();
    
    return defaultStocks.map(stock => {
      const savedPrice = savedStockPrices[stock.id];
      if (savedPrice) {
        return {
          ...stock,
          currentPrice: savedPrice.currentPrice,
          previousPrice: savedPrice.previousPrice,
          change: savedPrice.change,
          changePercent: savedPrice.changePercent,
          volume: savedPrice.volume,
          high24h: savedPrice.high24h,
          low24h: savedPrice.low24h,
          marketCap: savedPrice.marketCap,
          priceHistory: savedPrice.priceHistory
        };
      }
      return stock;
    });
  };

  // Hilfsfunktion zum sicheren Speichern mit Datenpunkt-Begrenzung
  const safeSaveStockPrices = (stocks: StockItem[]) => {
    try {
      // Begrenze Preisverlauf auf die aktuellsten 30 Datenpunkte pro Aktie
      const limitedStocks = stocks.map(stock => ({
        ...stock,
        priceHistory: stock.priceHistory.slice(-30) // Nur die aktuellsten 30 Datenpunkte
      }));
      
      saveStockPricesToStore(limitedStocks);
    } catch (error) {
      console.warn('Speicherfehler beim Speichern der Aktienpreise:', error);
    }
  };

  // Lade gespeicherte Aktienpreise beim Öffnen des Modals und initialisiere nur einmal
  useEffect(() => {
    if (isOpen) {
      const savedStockPrices = useGameStore.getState().getAllStockPrices();
      if (Object.keys(savedStockPrices).length > 0) {
        // Lade nur gespeicherte Preise, ohne die Standard-Daten zu überschreiben
        setStockMarket(prev => {
          const updatedStocks = prev.map(stock => {
            const savedPrice = savedStockPrices[stock.id];
            if (savedPrice) {
              return {
                ...stock,
                currentPrice: savedPrice.currentPrice,
                previousPrice: savedPrice.previousPrice,
                change: savedPrice.change,
                changePercent: savedPrice.changePercent,
                volume: savedPrice.volume,
                high24h: savedPrice.high24h,
                low24h: savedPrice.low24h,
                marketCap: savedPrice.marketCap,
                priceHistory: savedPrice.priceHistory || stock.priceHistory
              };
            }
            return stock;
          });
          return updatedStocks;
        });
      }
    }
  }, [isOpen]);

    // Verhindere Neustart der Börse bei jedem Öffnen
  const [hasInitialized, setHasInitialized] = useState(false);

  useEffect(() => {
    if (isOpen && !hasInitialized) {
      setHasInitialized(true);
    }
  }, [isOpen, hasInitialized]);

  // Speicher-Überwachung und Status-Anzeige (nur Monitoring)
  useEffect(() => {
    if (!isOpen) return;

    // Einmalige Speicher-Überprüfung beim Öffnen des Modals
    const stats = storageManager.getStorageStats();
    if (stats.isNearLimit) {
      console.log(`📊 Speicher-Status beim Öffnen der Börse: ${stats.percentage.toFixed(1)}%`);
      storageManager.logStorageInfo();
    }

    // Periodische Überprüfung während das Modal offen ist (nur Monitoring)
    const interval = setInterval(() => {
      const currentStats = storageManager.getStorageStats();
      if (currentStats.isNearLimit) {
        console.log(`📊 Börse: Speicher wird knapp: ${currentStats.percentage.toFixed(1)}%`);
      }
    }, 60000); // Alle 60 Sekunden prüfen

    return () => clearInterval(interval);
  }, [isOpen]);

  // Simuliere Börsenbewegungen mit erweiterten Daten (alle 3 Sekunden wie Chart)
  useEffect(() => {
    if (!isOpen || !hasInitialized) return;

    let saveCounter = 0; // Zähler für das Speichern

    const interval = setInterval(() => {
      setStockMarket(prev => {
        const updatedStocks = prev.map(stock => {
          const volatility = stock.type === 'gem' ? 0.08 : 0.05;
          const change = (Math.random() - 0.5) * volatility * stock.currentPrice;
          const newPrice = Math.max(0.01, stock.currentPrice + change);
          
          // Aktualisiere 24h High/Low
          const newHigh24h = Math.max(stock.high24h || 0, newPrice);
          const newLow24h = Math.min(stock.low24h || newPrice, newPrice);
          
          // Simuliere Volumen-Änderungen
          const volumeChange = (Math.random() - 0.5) * 0.2;
          const newVolume = Math.max(1000, stock.volume * (1 + volumeChange));
          
          // Füge neuen Preis zum Verlauf hinzu (keine Begrenzung)
          const newPriceHistory = [...stock.priceHistory, {
            timestamp: Date.now(),
            price: newPrice
          }];
          
          return {
            ...stock,
            previousPrice: stock.currentPrice,
            currentPrice: newPrice,
            change: newPrice - stock.previousPrice,
            changePercent: ((newPrice - stock.previousPrice) / stock.previousPrice) * 100,
            volume: Math.floor(newVolume),
            priceHistory: newPriceHistory,
            high24h: newHigh24h,
            low24h: newLow24h,
            marketCap: newPrice * Math.floor(newVolume)
          };
        });

        // Speichere alle 60 Sekunden (60 Updates) für bessere Performance und weniger Speicher-Stress
        saveCounter++;
        if (saveCounter >= 60) {
          safeSaveStockPrices(updatedStocks);
          saveCounter = 0;
        }
        
        return updatedStocks;
      });

      // Simuliere virtuelle Spieler jede Sekunde
      simulateVirtualPlayers();
    }, 1000);

    return () => clearInterval(interval);
  }, [isOpen, hasInitialized]);

  // Aktualisiere die Anzeige jede Sekunde für Live-Updates
  useEffect(() => {
    if (!isOpen) return;

    const interval = setInterval(() => {
      setUpdateTrigger(prev => prev + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [isOpen]);

  // Wenn das Chart offen ist, aktualisiere das übergebene `chartStock` jedes Update (jede Sekunde)
  useEffect(() => {
    if (!isChartModalOpen || !chartStock) return;
    const live = stockMarket.find(s => s.id === chartStock.id);
    if (live) setChartStock(live);
  }, [updateTrigger, isChartModalOpen, chartStock, stockMarket]);




 

   // Funktion zur Simulation von Marktteilnehmer-Reaktionen auf große Trades
  const MAX_NEGATIVE_IMPACT = -0.5; // nicht mehr als -50% pro Trade
  const MAX_POSITIVE_IMPACT = 0.3;  // nicht mehr als +30% pro Trade

  // Dynamische Berechnung der maximal erlaubten relativen Preisänderung pro Aktie
  // Größere Aktien (höheres marketCap/Volumen) erlauben größere absolute Schwankungen.
  const computeImpactLimits = (stock: any) => {
    const scaleSource = Math.max(stock.marketCap || 1, stock.volume || 1);
    // Skaliere logarithmisch, min 0.5x, max 3x
    const scale = Math.min(3, Math.max(0.5, Math.log10(scaleSource) / 3));
    // Basiswerte: konservativer Kern (5% up / -15% down) skaliert
    const maxPositive = 0.05 * scale; // z.B. 0.05..0.15
    const maxNegative = -0.15 * scale; // z.B. -0.075..-0.45
    return { maxNegative, maxPositive };
  };

  // Funktion zur Simulation von Marktteilnehmer-Reaktionen auf große Trades
  const simulateMarketReaction = (stockId: string, amount: number, type: 'buy' | 'sell', price: number) => {
    const tradeValue = amount * price;
    const isLargeTrade = tradeValue > 1000; // Große Trades über 1000 Münzen
    
    if (!isLargeTrade) return; // Nur bei großen Trades simulieren
    
    setStockMarket(prevMarket => {
      return prevMarket.map(stock => {
        if (stock.id === stockId) {
          let marketReaction = 0;
          let additionalVolume = 0;
          
          if (type === 'buy') {
            // Bei großen Käufen: Gewinnmitnahmen und FOMO
            const profitTaking = Math.random() * 0.3; // 0-30% Chance auf Gewinnmitnahmen
            const fomoBuying = Math.random() * 0.2; // 0-20% Chance auf FOMO-Käufe
            
            if (profitTaking > 0.15) {
              // Gewinnmitnahmen - Verkäufe durch andere Händler
              marketReaction = -0.005 * (amount / 1000); // Preis sinkt durch Verkäufe
              additionalVolume = Math.floor(amount * 0.3); // Zusätzliches Volumen durch Verkäufe
            } else if (fomoBuying > 0.1) {
              // FOMO-Käufe - andere Händler kaufen auch
              marketReaction = 0.003 * (amount / 1000); // Preis steigt weiter
              additionalVolume = Math.floor(amount * 0.2); // Zusätzliches Volumen durch Käufe
            }
          } else {
            // Bei großen Verkäufen: Panikverkäufe und Bottom-Fishing
            const panicSelling = Math.random() * 0.4; // 0-40% Chance auf Panikverkäufe
            const bottomFishing = Math.random() * 0.25; // 0-25% Chance auf Bottom-Fishing
            
            if (panicSelling > 0.2) {
              // Panikverkäufe - andere Händler verkaufen auch
              marketReaction = -0.008 * (amount / 1000); // Stärkerer Preisverfall
              additionalVolume = Math.floor(amount * 0.5); // Viel zusätzliches Volumen
            } else if (bottomFishing > 0.15) {
              // Bottom-Fishing - Händler kaufen günstig ein
              marketReaction = 0.002 * (amount / 1000); // Leichte Erholung
              additionalVolume = Math.floor(amount * 0.15); // Weniger Volumen
            }
          }
          
          // Begrenze die relative Marktreaktion basierend auf Aktie (marketCap/volume)
          const { maxNegative, maxPositive } = computeImpactLimits(stock);
          marketReaction = Math.max(Math.min(marketReaction, maxPositive), maxNegative);
          
          const newPrice = stock.currentPrice * (1 + marketReaction);
          const newVolume = stock.volume + amount + additionalVolume;
          const newMarketCap = newPrice * newVolume;
          
          return {
            ...stock,
            currentPrice: Math.max(0.01, newPrice),
            volume: newVolume,
            marketCap: newMarketCap,
            high24h: Math.max(stock.high24h, newPrice),
            low24h: Math.min(stock.low24h, newPrice)
          };
        }
        return stock;
      });
    });
  };

   // Funktion zur Aktualisierung der Marktdaten basierend auf Spieler-Trades
  const updateMarketDataFromTrade = (stockId: string, amount: number, type: 'buy' | 'sell', price: number) => {
    const tradeValue = amount * price;
    
    // Simuliere Marktteilnehmer-Reaktionen bei großen Trades
    if (tradeValue > 1000) {
      simulateMarketReaction(stockId, amount, type, price);
      return; // Die Simulation übernimmt die Marktaktualisierung
    }
    
    // Normale Marktaktualisierung für kleine Trades
    setStockMarket(prevMarket => {
      return prevMarket.map(stock => {
        if (stock.id === stockId) {
          const volumeImpact = amount;
          const newVolume = stock.volume + volumeImpact;
          
          // Kleine Preisänderung für normale Trades
          const priceImpact = type === 'buy' ? 0.001 : -0.001;
          // Begrenze auch hier die relative Änderung dynamisch je Aktie
          const relativeChange = priceImpact * (amount / 1000);
          const { maxNegative: smallMaxNeg, maxPositive: smallMaxPos } = computeImpactLimits(stock);
          const clampedRelativeChange = Math.max(Math.min(relativeChange, smallMaxPos), smallMaxNeg);
          const newPrice = stock.currentPrice * (1 + clampedRelativeChange);
          
          const newMarketCap = newPrice * newVolume;
          
          return {
            ...stock,
            currentPrice: Math.max(0.01, newPrice),
            volume: newVolume,
            marketCap: newMarketCap,
            high24h: Math.max(stock.high24h, newPrice),
            low24h: Math.min(stock.low24h, newPrice)
          };
        }
        return stock;
      });
    });
  };

   // Neue Trading-Funktion für das Modal
  const handleTradeFromModal = (stock: StockItem, amount: number, type: 'buy' | 'sell', currency: 'coins' | 'gems') => {
    // Verwende die Live-Preise aus dem stockMarket State
    const liveStock = stockMarket.find(s => s.id === stock.id);
    const currentPrice = liveStock?.currentPrice || stock.currentPrice;
    const totalCost = currentPrice * amount;
    
    console.log(`Trade-Versuch: ${type} ${amount} ${stock.name} für ${totalCost} ${currency}`);
    console.log(`Aktueller Preis: ${currentPrice}, Verfügbare ${currency}: ${currency === 'coins' ? coins : gems}`);
    
    if (type === 'buy') {
      // Prüfe verfügbare Anteile
      const availableShares = getAvailableShares(stock.id);
      console.log(`Verfügbare Anteile: ${availableShares}, Gewünscht: ${amount}`);
      
      // Wenn Aktie unbegrenzt verfügbar ist, überspringe die Verfügbarkeitsprüfung
      if (stock.id !== 'auto_graber_xdustries' && amount > availableShares) {
        console.log(`❌ Nicht genug Anteile verfügbar. Verfügbar: ${availableShares}, Gewünscht: ${amount}`);
        return;
      }
      
      // Prüfe Budget
      if (currency === 'coins' && coins < totalCost) {
        console.log(`❌ Nicht genug Münzen. Verfügbar: ${coins}, Benötigt: ${totalCost}`);
        return;
      }
      if (currency === 'gems' && gems < totalCost) {
        console.log(`❌ Nicht genug Edelsteine. Verfügbar: ${gems}, Benötigt: ${totalCost}`);
        return;
      }
      
      try {
        // Speichere Portfolio vor dem Trade
        const portfolioBefore = useGameStore.getState().getStockPortfolio();
        const positionBefore = portfolioBefore.find(pos => pos.stockId === stock.id && pos.currency === currency);
        const sharesBefore = positionBefore ? positionBefore.shares : 0;
        
        console.log(`📊 Portfolio vor Trade: ${sharesBefore} Anteile von ${stock.name}`);
        
        // Entferne die Währung vom Spieler
        if (currency === 'coins') {
          const success = spendCoins(totalCost);
          if (!success) {
            console.log(`❌ Fehler beim Abziehen der Münzen`);
            return;
          }
          console.log(`✅ ${totalCost} Münzen abgezogen`);
        } else {
          const success = spendGems(totalCost);
          if (!success) {
            console.log(`❌ Fehler beim Abziehen der Edelsteine`);
            return;
          }
          console.log(`✅ ${totalCost} Edelsteine abgezogen`);
        }
        
        // Verwende GameStore-Funktionen
        console.log(`📈 Füge ${amount} Anteile zum Portfolio hinzu...`);
        addStockToPortfolio(stock.id, amount, currency);
        
        console.log(`📝 Füge Trade zur Historie hinzu...`);
        addTradeToHistory(stock.id, 'buy', amount, currentPrice, currency);
        
        // Aktualisiere Marktdaten basierend auf dem Trade
        console.log(`🔄 Aktualisiere Marktdaten...`);
        updateMarketDataFromTrade(stock.id, amount, 'buy', currentPrice);
        
        console.log(`✅ Trade erfolgreich abgeschlossen!`);
        
        // Debug: Zeige aktuelles Portfolio
        const portfolio = useGameStore.getState().getStockPortfolio();
        console.log(`📊 Aktuelles Portfolio:`, portfolio);
        
        // Überprüfe, ob die Aktien tatsächlich hinzugefügt wurden
        const addedPosition = portfolio.find(pos => pos.stockId === stock.id && pos.currency === currency);
        const sharesAfter = addedPosition ? addedPosition.shares : 0;
        
        console.log(`📊 Portfolio nach Trade: ${sharesAfter} Anteile von ${stock.name}`);
        console.log(`📈 Änderung: ${sharesAfter - sharesBefore} Anteile`);
        
        if (addedPosition && sharesAfter > sharesBefore) {
          console.log(`✅ Position erfolgreich hinzugefügt: ${addedPosition.shares} Anteile von ${stock.name}`);
        } else {
          console.log(`❌ Position wurde NICHT zum Portfolio hinzugefügt!`);
          console.log(`Vorher: ${sharesBefore}, Nachher: ${sharesAfter}`);
        }
        
        // Aktualisiere die UI
        setUpdateTrigger(prev => prev + 1);
        
      } catch (error) {
        console.error(`❌ Fehler beim Trade:`, error);
        // Versuche, das Geld zurückzugeben
        if (currency === 'coins') {
          addCoins(totalCost);
        } else {
          addGems(totalCost);
        }
      }
    }
  };

  // Funktion zum Öffnen des Trading Modals
  const openTradingModal = (stock: StockItem) => {
    setTradingStock(stock);
    setIsTradingModalOpen(true);
  };

  const openTradingModalForStockId = (stockId: string) => {
    const stock = stockMarket.find(s => s.id === stockId);
    if (stock) {
      setTradingStock(stock);
      setIsTradingModalOpen(true);
    }
  };

  // Funktion zum Öffnen des Chart Modals
  const openChartModal = (stock: StockItem) => {
    setChartStock(stock);
    setIsChartModalOpen(true);
  };

  // Funktion zum Öffnen der Verkaufsbestätigung
  const openSellConfirmation = (stock: StockItem, position: { shares: number; currency: 'coins' | 'gems' }) => {
    setSellConfirmation({
      isOpen: true,
      stock: stock,
      position: position,
      sellAmount: position.shares // Standardmäßig alle Anteile
    });
  };

  // Funktion zum Verkaufen einer Position
  const sellPosition = (stock: StockItem, position: { shares: number; currency: 'coins' | 'gems' }) => {
    const liveStock = stockMarket.find(s => s.id === stock.id);
    const currentPrice = liveStock?.currentPrice || stock.currentPrice;
    const sellAmount = sellConfirmation.sellAmount;
    const totalValue = currentPrice * sellAmount;
    
    // Füge die Währung dem Spieler hinzu
    if (position.currency === 'coins') {
      addCoins(totalValue);
    } else {
      addGems(totalValue);
    }
    
    // Verwende GameStore-Funktionen
    removeStockFromPortfolio(stock.id, sellAmount, position.currency);
    addTradeToHistory(stock.id, 'sell', sellAmount, currentPrice, position.currency);
    
    // Aktualisiere Marktdaten basierend auf dem Verkauf
    updateMarketDataFromTrade(stock.id, sellAmount, 'sell', currentPrice);
    
    // Schließe die Bestätigung
    setSellConfirmation({
      isOpen: false,
      stock: null,
      position: null,
      sellAmount: 0
    });
  };

  const getPortfolioValue = () => {
    return portfolio.reduce((total, position) => {
      const stock = stockMarket.find(s => s.id === position.stockId);
      return total + (stock ? stock.currentPrice * position.shares : 0);
    }, 0);
  };

  const getPortfolioValueByCurrency = (currency: 'coins' | 'gems') => {
    return portfolio.reduce((total, position) => {
      if (position.currency !== currency) return total;
      const stock = stockMarket.find(s => s.id === position.stockId);
      return total + (stock ? stock.currentPrice * position.shares : 0);
    }, 0);
  };

  const getAvailableCurrency = () => {
    return selectedCurrency === 'coins' ? coins : gems;
  };

  const getCurrencyName = () => {
    return selectedCurrency === 'coins' ? 'Münzen' : 'Diamanten';
  };

  const getCurrencyIcon = () => {
    return selectedCurrency === 'coins' ? '🪙' : '💎';
  };

  const getChangeColor = (change: number) => {
    return change >= 0 ? 'text-green-400' : 'text-red-400';
  };

  const getChangeIcon = (change: number) => {
    return change >= 0 ? '↗️' : '↘️';
  };

  const getPortfolioPerformance = () => {
    const totalInvested = tradeHistory
      .filter(trade => trade.type === 'buy')
      .reduce((total, trade) => total + (trade.price * trade.amount), 0);
    
    const totalSold = tradeHistory
      .filter(trade => trade.type === 'sell')
      .reduce((total, trade) => total + (trade.price * trade.amount), 0);
    
    const currentValue = getPortfolioValue();
    const netProfit = currentValue + totalSold - totalInvested;
    const profitPercent = totalInvested > 0 ? (netProfit / totalInvested) * 100 : 0;
    
    return { netProfit, profitPercent };
  };

  const getPortfolioPerformanceByCurrency = (currency: 'coins' | 'gems') => {
    // Filtere Handelshistorie direkt nach Währung
    const tradesInCurrency = tradeHistory.filter(trade => trade.currency === currency);
    
    const totalInvested = tradesInCurrency
      .filter(trade => trade.type === 'buy')
      .reduce((total, trade) => total + (trade.price * trade.amount), 0);
    
    const totalSold = tradesInCurrency
      .filter(trade => trade.type === 'sell')
      .reduce((total, trade) => total + (trade.price * trade.amount), 0);
    
    const currentValue = getPortfolioValueByCurrency(currency);
    const netProfit = currentValue + totalSold - totalInvested;
    const profitPercent = totalInvested > 0 ? (netProfit / totalInvested) * 100 : 0;
    
    return { netProfit, profitPercent };
  };

  // Verfügbare Anteile pro Aktie (1 Million bis 1 Milliarde)
  const getAvailableShares = (stockId: string) => {
    // Verwende updateTrigger um die Funktion zu re-evaluieren
    const _ = updateTrigger;
    // Zufällige Basis-Anteile zwischen 1M und 1B pro Aktie
    const getBaseSharesForStock = (id: string) => {
      // Konsistente Zufallszahl basierend auf Stock-ID
      let hash = 0;
      for (let i = 0; i < id.length; i++) {
        const char = id.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32bit integer
      }
      const random = Math.abs(hash) / 2147483647; // Normalize to 0-1
      
      // Anteile zwischen 1M und 1B basierend auf Aktien-Typ
      const stock = stockMarket.find(s => s.id === id);
      if (stock?.type === 'gem') {
        // Diamanten-Aktien: 1M - 10M Anteile
        return Math.floor(1000000 + random * 9000000);
      } else {
        // Münzen-Aktien: 100M - 1B Anteile
        return Math.floor(100000000 + random * 900000000);
      }
    };
    
    // Spezialfall: Auto Gräber Xdustries hat unbegrenzte verfügbare Anteile
    if (stockId === 'auto_graber_xdustries') {
      return Infinity;
    }
    const baseShares = getBaseSharesForStock(stockId);
    const state = useGameStore.getState();
    const globalSharesOwned = state.stockMarket.globalMarket.totalSharesOwned[stockId] || 0;
    return Math.max(0, baseShares - globalSharesOwned);
  };

  // Berechne die Änderung der verfügbaren Anteile seit der letzten Aktualisierung
  const getAvailableSharesChange = (stockId: string) => {
    // Verwende updateTrigger um die Funktion zu re-evaluieren
    const _ = updateTrigger;
    const state = useGameStore.getState();
    const marketActivity = state.stockMarket.globalMarket.marketActivity;
    
    // Finde die letzten 2 Aktivitäten für diese Aktie
    const stockActivities = marketActivity
      .filter(activity => activity.stockId === stockId)
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
      .slice(0, 2);
    
    if (stockActivities.length < 2) return 0;
    
    const latestActivity = stockActivities[0];
    const previousActivity = stockActivities[1];
    
    // Berechne die Änderung basierend auf der Art der letzten Aktivität
    if (latestActivity.type === 'buy') {
      return -latestActivity.amount; // Negative Änderung bei Kauf
    } else if (latestActivity.type === 'sell') {
      return latestActivity.amount; // Positive Änderung bei Verkauf
    }
    
    return 0;
  };

  // Formatiere die Änderung mit Plus/Minus Symbol
  const formatSharesChange = (change: number) => {
    if (change === 0) return '';
    const sign = change > 0 ? '+' : '';
    return `${sign}${formatNumber(change)}`;
  };

  // Hole die letzte Aktivität für eine Aktie
  const getLastActivity = (stockId: string) => {
    // Verwende updateTrigger um die Funktion zu re-evaluieren
    const _ = updateTrigger;
    const state = useGameStore.getState();
    const marketActivity = state.stockMarket.globalMarket.marketActivity;
    
    const stockActivities = marketActivity
      .filter(activity => activity.stockId === stockId)
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    
    return stockActivities[0] || null;
  };

  // Formatiere die Zeit seit der letzten Aktivität
  const formatTimeSinceLastActivity = (timestamp: string) => {
    const now = Date.now();
    const activityTime = new Date(timestamp).getTime();
    const diffMs = now - activityTime;
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    
    if (diffMinutes < 1) {
      return `${diffSeconds}s`;
    } else if (diffMinutes < 60) {
      return `${diffMinutes}m`;
    } else {
      const diffHours = Math.floor(diffMinutes / 60);
      return `${diffHours}h`;
    }
  };

  const getFilteredStocks = () => {
    return stockMarket.filter(stock => {
      // Sektor-Filter
      if (selectedSector !== 'all' && stock.sector !== selectedSector) {
        return false;
      }
      
      // Such-Filter
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase().trim();
        const matchesName = stock.name.toLowerCase().includes(query);
        const matchesSymbol = stock.symbol.toLowerCase().includes(query);
        const matchesSector = stock.sector.toLowerCase().includes(query);
        
        if (!matchesName && !matchesSymbol && !matchesSector) {
          return false;
        }
      }
      
      return true;
    });
  };

  const sectors = ['all', 'Technology', 'Materials', 'Consumer', 'Energy', 'Utilities'];

  // Virtuelle Spieler simulieren
  // Funktion zur Analyse der aktuellen News und deren Auswirkung auf Trading-Entscheidungen
  const analyzeNewsImpact = (stockId: string) => {
    const state = useGameStore.getState();
    const marketActivity = state.stockMarket.globalMarket.marketActivity;
    
    // Suche nach den letzten 10 Aktivitäten für diese Aktie
    const recentActivities = marketActivity
      .filter(activity => activity.stockId === stockId)
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
      .slice(0, 10);
    
    // Analysiere die Stimmung basierend auf den letzten Trades
    let buyPressure = 0;
    let sellPressure = 0;
    
    recentActivities.forEach(activity => {
      if (activity.type === 'buy') {
        buyPressure += activity.amount;
      } else {
        sellPressure += activity.amount;
      }
    });
    
    // Berechne die Stimmung (positiv = mehr Käufe, negativ = mehr Verkäufe)
    const sentiment = buyPressure - sellPressure;
    const totalActivity = buyPressure + sellPressure;
    const sentimentRatio = totalActivity > 0 ? sentiment / totalActivity : 0;
    
    return {
      sentiment: sentimentRatio, // -1 bis 1
      buyPressure,
      sellPressure,
      totalActivity,
      hasRecentActivity: recentActivities.length > 0
    };
  };

  const simulateVirtualPlayers = () => {
    // Generiere 100 virtuelle Spieler-Namen
    const generateVirtualPlayerNames = () => {
      const prefixes = [
        'Trader', 'Stock', 'Investor', 'Market', 'Trade', 'Share', 'Profit', 'Bull', 'Bear', 'Crypto',
        'Finance', 'Capital', 'Wealth', 'Asset', 'Portfolio', 'Hedge', 'Arbitrage', 'Scalp', 'Swing', 'Day',
        'Quant', 'Algo', 'Bot', 'AI', 'Smart', 'Pro', 'Elite', 'Master', 'Guru', 'Wizard',
        'King', 'Queen', 'Prince', 'Duke', 'Lord', 'Sir', 'Madam', 'Baron', 'Knight', 'Warrior',
        'Hunter', 'Seeker', 'Finder', 'Explorer', 'Pioneer', 'Voyager', 'Navigator', 'Captain', 'Pilot', 'Driver'
      ];
      
      const suffixes = [
        'Max', 'Pro', 'Elite', 'Prime', 'Gold', 'Silver', 'Platinum', 'Diamond', 'Ruby', 'Sapphire',
        'Master', 'Guru', 'Wizard', 'King', 'Queen', 'Prince', 'Duke', 'Lord', 'Knight', 'Warrior',
        'Hunter', 'Seeker', 'Finder', 'Explorer', 'Pioneer', 'Voyager', 'Navigator', 'Captain', 'Pilot', 'Driver',
        'Alpha', 'Beta', 'Gamma', 'Delta', 'Omega', 'Sigma', 'Theta', 'Phi', 'Psi', 'Chi',
        'One', 'Two', 'Three', 'First', 'Second', 'Third', 'Ace', 'Star', 'Moon', 'Sun'
      ];

      const names = [];
      for (let i = 0; i < 100; i++) {
        const prefix = prefixes[Math.floor(Math.random() * prefixes.length)];
        const suffix = suffixes[Math.floor(Math.random() * suffixes.length)];
        const number = Math.floor(Math.random() * 999) + 1;
        names.push(`${prefix}${suffix}${number}`);
      }
      return names;
    };

    // Erstelle virtuelle Spieler, falls noch keine existieren
    const state = useGameStore.getState();
    if (state.stockMarket.globalMarket.virtualPlayers.length === 0) {
      const playerNames = generateVirtualPlayerNames();
      
      // Hole das aktuelle Budget des Spielers als Referenz
      const playerCoins = state.coins || 1000;
      const playerGems = state.gems || 100;
      
      playerNames.forEach((name, index) => {
        // Budget zwischen 1% und 100% des Spielers
        const budgetPercentage = (Math.random() * 0.99 + 0.01); // 1% bis 100%
        const budgetCoins = Math.floor(playerCoins * budgetPercentage);
        const budgetGems = Math.floor(playerGems * budgetPercentage);
        
        const virtualPlayer = {
          id: `virtual_${index}`,
          name,
          portfolio: [],
          lastTradeTime: new Date().toISOString(),
          budget: {
            coins: budgetCoins,
            gems: budgetGems
          },
          tradingStyle: (Math.random() > 0.3 ? 'aggressive' : 'conservative') as 'aggressive' | 'conservative', // 70% aggressive
          riskTolerance: Math.random() // 0-1, höher = risikofreudiger
        };
        
        useGameStore.setState((state) => ({
          stockMarket: {
            ...state.stockMarket,
            globalMarket: {
              ...state.stockMarket.globalMarket,
              virtualPlayers: [...state.stockMarket.globalMarket.virtualPlayers, virtualPlayer]
            }
          }
        }));
      });
    }

    // Simuliere zufällige Trades von virtuellen Spielern
    const now = Date.now();
    const lastActivity = state.stockMarket.globalMarket.marketActivity[state.stockMarket.globalMarket.marketActivity.length - 1];
    const timeSinceLastActivity = lastActivity ? now - new Date(lastActivity.timestamp).getTime() : 60000;

    // Jede 1000ms mehrere virtuelle Trades (moderate Aktivität mit 100 Spielern)
    if (timeSinceLastActivity > 1000) { // Alle 1000ms
      // Simuliere 5-15 Trades gleichzeitig für moderate Aktivität
      const numberOfTrades = Math.floor(Math.random() * 11) + 5; // 5-15 Trades
      
      for (let tradeIndex = 0; tradeIndex < numberOfTrades; tradeIndex++) {
        // Sicherheitsprüfungen
        if (!stockMarket || stockMarket.length === 0) continue;
        if (!state.stockMarket.globalMarket.virtualPlayers || state.stockMarket.globalMarket.virtualPlayers.length === 0) continue;
        
        const randomStock = stockMarket[Math.floor(Math.random() * stockMarket.length)];
        const randomVirtualPlayer = state.stockMarket.globalMarket.virtualPlayers[Math.floor(Math.random() * state.stockMarket.globalMarket.virtualPlayers.length)];
        
        // Sicherheitsprüfung für Spieler
        if (!randomVirtualPlayer || !randomStock) continue;
        
        // Bestimme Trading-Stil basierend auf Spieler-Eigenschaften
        const isAggressive = randomVirtualPlayer.tradingStyle === 'aggressive';
        const riskTolerance = randomVirtualPlayer.riskTolerance || 0.5; // Fallback-Wert
        
        // Analysiere News-Impact und Marktstimmung für diese Aktie
        const newsImpact = analyzeNewsImpact(randomStock.id);
        
        // Trade-Typ basierend auf Risikotoleranz, Marktbedingungen und News
        let tradeType: 'buy' | 'sell';
        let newsInfluence = 0;
        
        // Berücksichtige News-Impact bei Trading-Entscheidungen
        if (newsImpact.hasRecentActivity) {
          // News-basierte Trading-Entscheidungen (aggressiver)
          if (newsImpact.sentiment > 0.3) {
            // Positive Stimmung - mehr Käufe
            newsInfluence = 0.4;
            if (isAggressive) {
              tradeType = Math.random() > 0.1 ? 'buy' : 'sell'; // 90% Kauf-Chance
            } else {
              tradeType = Math.random() > 0.3 ? 'buy' : 'sell'; // 70% Kauf-Chance
            }
          } else if (newsImpact.sentiment < -0.3) {
            // Negative Stimmung - mehr Verkäufe
            newsInfluence = -0.4;
            if (isAggressive) {
              tradeType = Math.random() > 0.5 ? 'buy' : 'sell'; // 50% Kauf-Chance
            } else {
              tradeType = Math.random() > 0.7 ? 'buy' : 'sell'; // 30% Kauf-Chance
            }
          } else {
            // Neutrale Stimmung - aggressivere Trading-Logik
            if (isAggressive) {
              tradeType = Math.random() > 0.3 ? 'buy' : 'sell'; // 70% Kauf-Chance
            } else {
              tradeType = Math.random() > 0.6 ? 'buy' : 'sell'; // 40% Kauf-Chance
            }
          }
        } else {
          // Keine recent activity - aggressivere Trading-Logik
          if (isAggressive) {
            tradeType = Math.random() > 0.3 ? 'buy' : 'sell'; // 70% Kauf-Chance
          } else {
            tradeType = Math.random() > 0.6 ? 'buy' : 'sell'; // 40% Kauf-Chance
          }
        }
        
        // Trade-Größe basierend auf Budget und Trading-Stil
        const playerBudget = randomVirtualPlayer.budget;
        
        // Sicherheitsprüfung für bestehende Spieler ohne Budget
        if (!playerBudget || !playerBudget.coins || !playerBudget.gems) {
          // Überspringe diesen Trade für Spieler ohne Budget
          continue;
        }
        
        const stockPrice = randomStock.currentPrice || 1; // Fallback-Preis
        const currency = Math.random() > 0.5 ? 'coins' : 'gems';
        const availableBudget = currency === 'coins' ? playerBudget.coins : playerBudget.gems;
        
        // Berechne maximale Anteile basierend auf Budget
        const maxAffordableShares = Math.floor(availableBudget / stockPrice);
        
        // Sicherheitsprüfung für gültige Trade-Berechnung
        if (maxAffordableShares <= 0 || availableBudget <= 0) {
          continue;
        }
        
        // Trade-Größe basierend auf Budget-Prozent, Trading-Stil und News-Impact
        let budgetPercentage: number;
        let newsMultiplier = 1.0;
        
        // Berücksichtige News-Impact bei Trade-Größe
        if (Math.abs(newsInfluence) > 0.1) {
          // Erhöhe Trade-Größe bei starker News-Reaktion
          newsMultiplier = 1.0 + Math.abs(newsInfluence) * 0.5; // Bis zu 50% größere Trades
        }
        
        if (isAggressive) {
          budgetPercentage = (0.15 + (riskTolerance * 0.6)) * newsMultiplier; // 15-75% des Budgets + News-Bonus
        } else {
          budgetPercentage = (0.1 + (riskTolerance * 0.25)) * newsMultiplier; // 10-35% des Budgets + News-Bonus
        }
        
        const maxTradeAmount = Math.floor(maxAffordableShares * budgetPercentage);
        const minTradeAmount = Math.max(25, Math.floor(maxTradeAmount * 0.1)); // Mindestens 25 Anteile für größere Trades
        
        // Sicherheitsprüfung für gültige Trade-Größe
        if (maxTradeAmount < minTradeAmount) {
          continue;
        }
        
        const tradeAmount = Math.floor(Math.random() * (maxTradeAmount - minTradeAmount + 1)) + minTradeAmount;

        // Debug-Ausgabe für die ersten paar Trades mit News-Impact
        if (tradeIndex < 3) {
          const newsInfo = newsImpact.hasRecentActivity 
            ? ` (News: ${newsImpact.sentiment.toFixed(2)}, ${newsInfluence > 0 ? '+' : ''}${newsInfluence.toFixed(2)})`
            : ' (Keine News)';
          console.log(`Virtueller Trade: ${randomVirtualPlayer.name} ${tradeType} ${tradeAmount} ${currency} von ${randomStock.name}${newsInfo}`);
        }

      // Prüfe verfügbare Anteile für Kauf
      if (tradeType === 'buy') {
        const availableShares = getAvailableShares(randomStock.id);
        if (tradeAmount <= availableShares) {
          // Kosten berechnen und Budget prüfen
          const cost = Math.floor(tradeAmount * stockPrice);

          useGameStore.setState((state) => ({
            stockMarket: {
              ...state.stockMarket,
              globalMarket: {
                ...state.stockMarket.globalMarket,
                virtualPlayers: state.stockMarket.globalMarket.virtualPlayers.map(player => {
                  if (player.id === randomVirtualPlayer.id) {
                    const existingPosition = player.portfolio.find(pos => pos.stockId === randomStock.id && pos.currency === currency);
                    const newBudgetValue = Math.max(0, (player.budget && player.budget[currency] ? player.budget[currency] : 0) - cost);

                    return {
                      ...player,
                      portfolio: existingPosition
                        ? player.portfolio.map(pos =>
                            pos.stockId === randomStock.id && pos.currency === currency
                              ? { ...pos, shares: pos.shares + tradeAmount }
                              : pos
                          )
                        : [...player.portfolio, { stockId: randomStock.id, shares: tradeAmount, currency }],
                      budget: {
                        ...player.budget,
                        [currency]: newBudgetValue
                      },
                      lastTradeTime: new Date().toISOString()
                    };
                  }
                  return player;
                })
              }
            }
          }));

          // Aktualisiere globale Anteile und Activity
          const currentTotal = state.stockMarket.globalMarket.totalSharesOwned[randomStock.id] || 0;
          const newTotal = currentTotal + tradeAmount;

          useGameStore.setState((state) => ({
            stockMarket: {
              ...state.stockMarket,
              globalMarket: {
                ...state.stockMarket.globalMarket,
                totalSharesOwned: {
                  ...state.stockMarket.globalMarket.totalSharesOwned,
                  [randomStock.id]: newTotal
                },
                marketActivity: [...state.stockMarket.globalMarket.marketActivity, {
                  id: `activity_${Date.now()}_${Math.random()}`,
                  stockId: randomStock.id,
                  type: 'buy' as const,
                  amount: tradeAmount,
                  price: randomStock.currentPrice,
                  timestamp: new Date().toISOString(),
                  currency,
                  playerType: 'virtual' as const
                }]
              }
            }
          }));
        }
      } else {
        // Führe virtuellen Verkauf durch
        const playerPortfolio = randomVirtualPlayer.portfolio.find(pos => pos.stockId === randomStock.id && pos.currency === currency);
        if (playerPortfolio && playerPortfolio.shares >= tradeAmount) {
          const proceeds = Math.floor(tradeAmount * stockPrice);

          useGameStore.setState((state) => ({
            stockMarket: {
              ...state.stockMarket,
              globalMarket: {
                ...state.stockMarket.globalMarket,
                virtualPlayers: state.stockMarket.globalMarket.virtualPlayers.map(player => {
                  if (player.id === randomVirtualPlayer.id) {
                    return {
                      ...player,
                      portfolio: player.portfolio.map(pos =>
                        pos.stockId === randomStock.id && pos.currency === currency
                          ? { ...pos, shares: Math.max(0, pos.shares - tradeAmount) }
                          : pos
                      ),
                      budget: {
                        ...player.budget,
                        [currency]: (player.budget && player.budget[currency] ? player.budget[currency] : 0) + proceeds
                      },
                      lastTradeTime: new Date().toISOString()
                    };
                  }
                  return player;
                })
              }
            }
          }));

          // Aktualisiere globale Anteile und Activity
          const currentTotal = state.stockMarket.globalMarket.totalSharesOwned[randomStock.id] || 0;
          const newTotal = Math.max(0, currentTotal - tradeAmount);

          useGameStore.setState((state) => ({
            stockMarket: {
              ...state.stockMarket,
              globalMarket: {
                ...state.stockMarket.globalMarket,
                totalSharesOwned: {
                  ...state.stockMarket.globalMarket.totalSharesOwned,
                  [randomStock.id]: newTotal
                },
                marketActivity: [...state.stockMarket.globalMarket.marketActivity, {
                  id: `activity_${Date.now()}_${Math.random()}`,
                  stockId: randomStock.id,
                  type: 'sell' as const,
                  amount: tradeAmount,
                  price: randomStock.currentPrice,
                  timestamp: new Date().toISOString(),
                  currency,
                  playerType: 'virtual' as const
                }]
              }
            }
          }));
        }
      }
    }
  };
  };

  return (
    <AnimatePresence key="stock-market-main">
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0, y: 50 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0, y: 50 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="bg-gradient-to-br from-gray-900 via-blue-900 to-indigo-900 rounded-2xl border-2 border-blue-500/50 shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-6 border-b border-blue-500/30 bg-gradient-to-r from-blue-900/50 to-indigo-900/50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-3xl">📈</span>
                  <h2 className="text-2xl font-bold text-white">Garten-Börse</h2>
                </div>
                <div className="flex items-center gap-4">
                  {/* Börsen-Speicherstatus verborgen */}
                  
                  <motion.button
                    onClick={onClose}
                    className="text-gray-400 hover:text-white text-2xl transition-colors"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    ✕
                  </motion.button>
                </div>
              </div>
            </div>

            {/* News-Ticker */}
            <div className="bg-black/40 border-b border-blue-500/30 p-3">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-red-500 text-sm font-bold animate-pulse">🔴 LIVE</span>
                <span className="text-xs text-gray-300">Markt-Nachrichten</span>
              </div>
              

              
              {/* Aktuelle News */}
              {currentNews && currentNewsData && (
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  className={`text-sm font-medium p-2 rounded border-l-4 cursor-pointer transition-all duration-200 hover:scale-105 ${
                    currentNewsData.type === 'positive' 
                      ? 'text-green-300 bg-green-900/20 border-green-500 hover:bg-green-900/30' 
                      : currentNewsData.type === 'negative'
                      ? 'text-red-300 bg-red-900/20 border-red-500 hover:bg-red-900/30'
                      : 'text-yellow-300 bg-yellow-900/20 border-yellow-500 hover:bg-yellow-900/30'
                  }`}
                  onClick={() => openTradingModalForStockId(currentNewsData.stockId)}
                  title={`Klicke um ${currentNewsData.stockName} zu handeln`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-xs opacity-75">
                      {currentNewsData.type === 'positive' ? '📈' : currentNewsData.type === 'negative' ? '📉' : '📊'}
                    </span>
                    <span>{currentNews}</span>
                    <span className="text-xs opacity-75 ml-auto">💱</span>
                  </div>
                </motion.div>
              )}
              
              {/* Fallback - Zeige immer etwas an */}
              {!currentNews && (
                <div className="text-xs text-gray-400 bg-gray-800/50 p-2 rounded">
                  📊 Warte auf Markt-Nachrichten...
                </div>
              )}
              {/* Virtuelle Trades Ticker (rechts->links) */}
              <div className="mt-2 overflow-hidden w-full">
                <div className="flex items-center">
                  <div className="whitespace-nowrap marquee flex gap-8">
                    {marketActivity && marketActivity.length > 0 ? (
                      marketActivity
                        .slice(-50)
                        .map((act: any) => {
                          const s = stockMarket.find(s => s.id === act.stockId);
                          const sym = s ? s.symbol : act.stockId;
                          const verb = act.type === 'buy' ? 'kauft' : 'verkauft';
                          const playerEmoji = act.playerType === 'virtual' ? '🤖' : '👤';
                          const colorClass = act.type === 'buy' ? 'text-green-300' : 'text-red-300';
                          return (
                            <span key={act.id} className={`text-xs ${colorClass}`}>
                              {playerEmoji} {verb} {formatNumber(act.amount)} {sym} @ {formatNumber(act.price)}
                            </span>
                          );
                        })
                    ) : (
                      <span className="text-xs text-gray-400">Keine Marktaktivität</span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-blue-500/30">
              {[
                { id: 'market', name: 'Markt', icon: '📊' },
                { id: 'portfolio', name: 'Portfolio', icon: '💼' },
                { id: 'history', name: 'Historie', icon: '📋' }
              ].map(tab => (
                <motion.button
                  key={tab.id}
                  onClick={() => setSelectedTab(tab.id as any)}
                  className={`flex-1 py-3 px-4 flex items-center justify-center gap-2 text-sm font-medium transition-colors ${
                    selectedTab === tab.id
                      ? 'text-blue-400 border-b-2 border-blue-400 bg-blue-900/20'
                      : 'text-gray-400 hover:text-gray-300'
                  }`}
                  whileHover={{ backgroundColor: 'rgba(59, 130, 246, 0.1)' }}
                >
                  <span>{tab.icon}</span>
                  {tab.name}
                </motion.button>
              ))}
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {selectedTab === 'market' && (
                <div className="space-y-4">

                  {/* Suchfeld */}
                  <div className="mb-4">
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-400 text-sm">🔍</span>
                      </div>
                      <input
                        type="text"
                        placeholder="Suche nach Aktien, Symbolen oder Sektoren..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                      />
                      {searchQuery && (
                        <button
                          onClick={() => setSearchQuery('')}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-300 transition-colors"
                        >
                          <span className="text-sm">✕</span>
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Sector Filter - Mobil optimiert */}
                  <div className="mb-3">
                    <div className="flex flex-wrap gap-1 justify-center">
                      {sectors.map(sector => (
                        <button
                          key={sector}
                          onClick={() => setSelectedSector(sector)}
                          className={`px-2 py-1.5 rounded-md font-medium transition-colors text-xs flex-1 max-w-20 ${
                            selectedSector === sector
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                          }`}
                        >
                          {sector === 'all' ? 'Alle' : sector}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Suchergebnisse Anzeige */}
                  {searchQuery && (
                    <div className="text-sm text-gray-400 mb-2">
                      {getFilteredStocks().length} von {stockMarket.length} Aktien gefunden
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {getFilteredStocks().map(stock => {
                      // Finde die Live-Daten für diese Aktie
                      const liveStock = stockMarket.find(s => s.id === stock.id);
                      const currentPrice = liveStock?.currentPrice || stock.currentPrice;
                      const changePercent = liveStock?.changePercent || stock.changePercent;
                      const volume = liveStock?.volume || stock.volume;
                      const high24h = liveStock?.high24h || stock.high24h;
                      const low24h = liveStock?.low24h || stock.low24h;
                      const marketCap = currentPrice * volume;
                      
                      return (
                        <motion.div
                          key={stock.id}
                          className="p-3 sm:p-4 rounded-xl border border-gray-600 bg-gray-800/50 hover:border-gray-500 hover:bg-gray-700/50"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <span className="text-xl sm:text-2xl flex-shrink-0">{stock.icon}</span>
                              <div className="min-w-0 flex-1">
                                <div className="font-bold text-white text-sm sm:text-base truncate">{stock.name}</div>
                                <div className="text-xs text-gray-400">{stock.symbol} • {stock.sector}</div>
                              </div>
                            </div>
                            <div className="text-right flex-shrink-0 ml-2">
                              <div className="font-bold text-white text-sm sm:text-base">{formatNumber(currentPrice)}</div>
                              <div className={`text-xs ${getChangeColor(changePercent)}`}>
                                {getChangeIcon(changePercent)} {changePercent.toFixed(2)}%
                              </div>
                            </div>
                          </div>
                          <div className="text-xs text-gray-400 mb-2 line-clamp-2">
                            {stock.description}
                          </div>
                          <div className="grid grid-cols-1 gap-1 text-xs text-gray-400">
                            <div>Vol: {formatNumber(volume)}</div>
                            <div>MK: {formatNumber(marketCap)}</div>
                            <div>24h: {formatNumber(low24h)}-{formatNumber(high24h)}</div>
                            <div className="flex items-center gap-1">
                              <span>Verfügbar: {getAvailableShares(stock.id) === Infinity ? 'Unbegrenzt' : formatNumber(getAvailableShares(stock.id))}</span>
                              {(() => {
                                const change = getAvailableSharesChange(stock.id);
                                const changeText = formatSharesChange(change);
                                if (changeText) {
                                  return (
                                    <motion.span 
                                      key={`${stock.id}-${updateTrigger}`}
                                      initial={{ opacity: 0, scale: 0.8 }}
                                      animate={{ opacity: 1, scale: 1 }}
                                      className={`text-xs font-medium ${change > 0 ? 'text-green-400' : 'text-red-400'}`}
                                    >
                                      ({changeText})
                                    </motion.span>
                                  );
                                }
                                return null;
                              })()}
                            </div>
                            {(() => {
                              const lastActivity = getLastActivity(stock.id);
                              if (lastActivity) {
                                const timeAgo = formatTimeSinceLastActivity(lastActivity.timestamp);
                                const activityType = lastActivity.type === 'buy' ? 'Kauf' : 'Verkauf';
                                const playerType = lastActivity.playerType === 'virtual' ? '🤖' : '👤';
                                return (
                                  <div className="text-xs text-gray-500">
                                    {playerType} {activityType} vor {timeAgo}
                                  </div>
                                );
                              }
                              return null;
                            })()}
                          </div>
                          
                          {/* Action Buttons */}
                          <div className="mt-3 flex gap-2">
                            <motion.button
                              onClick={() => openTradingModal(stock)}
                              className="flex-1 py-2 px-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg text-xs font-medium hover:from-blue-700 hover:to-indigo-700 transition-all"
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                            >
                              💰 Handeln
                            </motion.button>
                            <motion.button
                              onClick={() => openChartModal(stock)}
                              className="py-2 px-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg text-xs font-medium hover:from-green-700 hover:to-emerald-700 transition-all"
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                            >
                              📈
                            </motion.button>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                </div>
              )}



              {selectedTab === 'portfolio' && (
                <div className="space-y-4">
                  {/* Mobile-optimierte Infoboxen */}
                  <div className="grid grid-cols-2 gap-3">
                    {/* Portfolio-Wert */}
                    <div className="p-3 bg-gradient-to-r from-green-800/20 to-blue-800/20 rounded-xl border border-green-500/30">
                      <h3 className="text-sm font-bold text-white mb-2">Portfolio-Wert</h3>
                      <div className="text-lg font-bold text-green-400 mb-1">
                        {formatNumber(getPortfolioValueByCurrency('coins'))} 🪙
                      </div>
                      <div className="text-lg font-bold text-purple-400">
                        {formatNumber(getPortfolioValueByCurrency('gems'))} 💎
                      </div>
                      <div className="text-xs text-gray-400 mt-1">
                        Gesamt: {formatNumber(getPortfolioValue())}
                      </div>
                    </div>
                    
                    {/* Gesamtgewinn */}
                    <div className="p-3 bg-gradient-to-r from-blue-800/20 to-indigo-800/20 rounded-xl border border-blue-500/30">
                      <h3 className="text-sm font-bold text-white mb-2">Gesamtgewinn</h3>
                      <div className={`text-lg font-bold mb-1 ${getPortfolioPerformanceByCurrency('coins').netProfit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {formatNumber(Number(getPortfolioPerformanceByCurrency('coins').netProfit))} 🪙
                      </div>
                      <div className={`text-lg font-bold ${getPortfolioPerformanceByCurrency('gems').netProfit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {formatNumber(Number(getPortfolioPerformanceByCurrency('gems').netProfit))} 💎
                      </div>
                    </div>
                    
                    {/* Gewinn % */}
                    <div className="p-3 bg-gradient-to-r from-purple-800/20 to-pink-800/20 rounded-xl border border-purple-500/30">
                      <h3 className="text-sm font-bold text-white mb-2">Gewinn %</h3>
                      <div className={`text-lg font-bold mb-1 ${getPortfolioPerformanceByCurrency('coins').profitPercent >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {getPortfolioPerformanceByCurrency('coins').profitPercent.toFixed(2)}% 🪙
                      </div>
                      <div className={`text-lg font-bold ${getPortfolioPerformanceByCurrency('gems').profitPercent >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {getPortfolioPerformanceByCurrency('gems').profitPercent.toFixed(2)}% 💎
                      </div>
                    </div>
                    
                    {/* Aktive Positionen */}
                    <div className="p-3 bg-gradient-to-r from-yellow-800/20 to-orange-800/20 rounded-xl border border-yellow-500/30">
                      <h3 className="text-sm font-bold text-white mb-2">Positionen</h3>
                      <div className="text-xl font-bold text-yellow-400">
                        {formatNumber(portfolio.filter(position => position.shares > 0).length)}
                      </div>
                      <div className="text-xs text-gray-400 mt-1">
                        🪙 {formatNumber(portfolio.filter(position => position.shares > 0 && position.currency === 'coins').length)}
                      </div>
                      <div className="text-xs text-gray-400">
                        💎 {formatNumber(portfolio.filter(position => position.shares > 0 && position.currency === 'gems').length)}
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    {portfolio.map((position) => {
                      const stock = stockMarket.find(s => s.id === position.stockId);
                      if (!stock || position.shares <= 0) return null;
                      
                      const value = stock.currentPrice * position.shares;
                      const avgBuyPrice = tradeHistory
                        .filter(trade => trade.stockId === position.stockId && trade.type === 'buy')
                        .reduce((total, trade) => total + trade.price, 0) / 
                        tradeHistory.filter(trade => trade.stockId === position.stockId && trade.type === 'buy').length || 0;
                      
                      const profit = (stock.currentPrice - avgBuyPrice) * position.shares;
                      const profitPercent = avgBuyPrice > 0 ? ((stock.currentPrice - avgBuyPrice) / avgBuyPrice) * 100 : 0;
                      
                      const currencyIcon = position.currency === 'coins' ? '🪙' : '💎';
                      
                      return (
                        <motion.div 
                          key={position.id} 
                          className="p-3 bg-gray-800/50 rounded-xl border border-gray-600 hover:border-gray-500 hover:bg-gray-700/50 cursor-pointer group"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => openTradingModal(stock)}
                          title="Klicke um mehr zu kaufen"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <span className="text-xl flex-shrink-0">{stock.icon}</span>
                              <div className="min-w-0 flex-1">
                                <div className="font-bold text-white text-sm truncate">{stock.name}</div>
                                <div className="text-xs text-gray-400">{formatNumber(position.shares)} Anteile • {stock.sector}</div>
                              </div>
                              <span className="text-xs text-gray-500 group-hover:text-gray-300 transition-colors">💱</span>
                            </div>
                            <div className="text-right flex-shrink-0 ml-2">
                              <div className="font-bold text-white text-sm">{formatNumber(value)} {currencyIcon}</div>
                              <div className={`text-xs ${profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                {profit >= 0 ? '+' : ''}{formatNumber(profit)} {currencyIcon} ({profitPercent.toFixed(2)}%)
                              </div>
                            </div>
                          </div>
                          
                          {/* Verkaufsbutton - Mobile optimiert */}
                          <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                            <motion.button
                              onClick={() => openSellConfirmation(stock, position)}
                              className="flex-1 py-2 px-3 bg-gradient-to-r from-red-600 to-pink-600 text-white rounded-lg text-xs font-medium hover:from-red-700 hover:to-pink-700 transition-all"
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                            >
                              💰 Verkaufen
                            </motion.button>
                            <motion.button
                              onClick={() => openChartModal(stock)}
                              className="py-2 px-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg text-xs font-medium hover:from-green-700 hover:to-emerald-700 transition-all"
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                            >
                              📈 Chart
                            </motion.button>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                </div>
              )}

              {selectedTab === 'history' && (
                <div className="space-y-2">
                  {tradeHistory.length === 0 ? (
                    <div className="text-center text-gray-400 py-8">
                      Noch keine Handelsaktivität
                    </div>
                  ) : (
                    tradeHistory.slice().reverse().map(trade => {
                      const stock = stockMarket.find(s => s.id === trade.stockId);
                      return (
                        <div key={trade.id} className="p-3 bg-gray-800/50 rounded-xl border border-gray-600">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <span className="text-xl flex-shrink-0">{stock?.icon}</span>
                              <div className="min-w-0 flex-1">
                                <div className="font-bold text-white text-sm truncate">
                                  {trade.type === 'buy' ? 'Gekauft' : 'Verkauft'} {formatNumber(trade.amount)} {stock?.name}
                                </div>
                                <div className="text-xs text-gray-400">
                                  {new Date(trade.timestamp).toLocaleString()}
                                </div>
                              </div>
                            </div>
                            <div className="text-right flex-shrink-0 ml-2">
                              <div className={`font-bold text-sm ${trade.type === 'buy' ? 'text-green-400' : 'text-red-400'}`}>
                                {trade.type === 'buy' ? '-' : '+'}{formatNumber(trade.price * trade.amount)} {trade.currency === 'coins' ? '🪙' : '💎'}
                              </div>
                              <div className="text-xs text-gray-400">
                                {formatNumber(trade.price)} {trade.currency === 'coins' ? '🪙' : '💎'} pro Anteil
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
      
      {/* Trading Modal */}
      <TradingModal
        isOpen={isTradingModalOpen}
        onClose={() => {
          setIsTradingModalOpen(false);
          setTradingStock(null);
        }}
        stock={tradingStock}
        onTrade={handleTradeFromModal}
        availableCoins={coins}
        availableGems={gems}
        formatNumber={formatNumber}
        stockMarket={stockMarket}
      />
      
      {/* Chart Modal */}
      <ChartModal
        isOpen={isChartModalOpen}
        onClose={() => {
          setIsChartModalOpen(false);
          setChartStock(null);
        }}
        stock={chartStock}
        formatNumber={formatNumber}
      />
      
      {/* Verkaufsbestätigungsmodal */}
      {sellConfirmation.isOpen && sellConfirmation.stock && sellConfirmation.position && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[70] flex items-center justify-center p-4"
          onClick={() => setSellConfirmation({ isOpen: false, stock: null, position: null, sellAmount: 0 })}
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0, y: 50 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0, y: 50 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="bg-gradient-to-br from-gray-900 via-red-900 to-pink-900 rounded-2xl border-2 border-red-500/50 shadow-2xl max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-6 border-b border-red-500/30 bg-gradient-to-r from-red-900/50 to-pink-900/50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">⚠️</span>
                  <h3 className="text-lg font-bold text-white">Position verkaufen</h3>
                </div>
                <motion.button
                  onClick={() => setSellConfirmation({ isOpen: false, stock: null, position: null, sellAmount: 0 })}
                  className="text-gray-400 hover:text-white text-xl transition-colors"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  ✕
                </motion.button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              <div className="text-center">
                <div className="flex items-center justify-center gap-3 mb-4">
                  <span className="text-3xl">{sellConfirmation.stock.icon}</span>
                  <div>
                    <div className="font-bold text-white text-lg">{sellConfirmation.stock.name}</div>
                    <div className="text-sm text-gray-400">{sellConfirmation.stock.symbol}</div>
                  </div>
                </div>
                
                <div className="text-gray-300 mb-4">
                  Wieviele Anteile von {sellConfirmation.stock.name} möchtest du verkaufen?
                </div>
                
                {/* Mengenauswahl */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Anzahl Anteile
                  </label>
                  <input
                    type="number"
                    value={sellConfirmation.sellAmount}
                    onChange={(e) => {
                      const value = Math.min(sellConfirmation.position!.shares, Math.max(0, Number(e.target.value)));
                      setSellConfirmation(prev => ({ ...prev, sellAmount: value }));
                    }}
                    className="w-full px-3 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:border-red-400 focus:outline-none text-center"
                    placeholder="0"
                    min="0"
                    max={sellConfirmation.position!.shares}
                  />
                </div>
                
                {/* Quick Amount Buttons */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Schnell-Auswahl
                  </label>
                  <div className="grid grid-cols-4 gap-2">
                    {[250, 500, 1000, 5000].map((amount, index) => (
                      <button
                        key={`sell-${amount}-${index}`}
                        onClick={() => {
                          const sellAmount = Math.min(amount, sellConfirmation.position!.shares);
                          setSellConfirmation(prev => ({ ...prev, sellAmount: sellAmount }));
                        }}
                        className="py-2 px-3 bg-gray-600 text-gray-300 hover:bg-gray-500 rounded-lg text-sm font-medium transition-colors"
                      >
                        {amount}
                      </button>
                    ))}
                  </div>
                </div>
                
                <div className="bg-gray-800/50 rounded-lg p-4 mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-300">Aktueller Preis:</span>
                    <span className="font-bold text-white">
                      {formatNumber(sellConfirmation.stock.currentPrice)} {sellConfirmation.position.currency === 'coins' ? '🪙' : '💎'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-300">Verfügbare Anteile:</span>
                    <span className="font-bold text-white">
                      {sellConfirmation.position.shares}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300">Verkaufswert:</span>
                    <span className="font-bold text-green-400">
                      {formatNumber(sellConfirmation.stock.currentPrice * sellConfirmation.sellAmount)} {sellConfirmation.position.currency === 'coins' ? '🪙' : '💎'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <motion.button
                  onClick={() => setSellConfirmation({ isOpen: false, stock: null, position: null, sellAmount: 0 })}
                  className="flex-1 py-3 bg-gray-600 text-white rounded-lg font-medium hover:bg-gray-500 transition-colors"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Abbrechen
                </motion.button>
                <motion.button
                  onClick={() => sellPosition(sellConfirmation.stock!, sellConfirmation.position!)}
                  disabled={sellConfirmation.sellAmount <= 0 || sellConfirmation.sellAmount > sellConfirmation.position!.shares}
                  className="flex-1 py-3 bg-gradient-to-r from-red-600 to-pink-600 text-white rounded-lg font-medium hover:from-red-700 hover:to-pink-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  whileHover={{ scale: sellConfirmation.sellAmount > 0 && sellConfirmation.sellAmount <= sellConfirmation.position!.shares ? 1.02 : 1 }}
                  whileTap={{ scale: sellConfirmation.sellAmount > 0 && sellConfirmation.sellAmount <= sellConfirmation.position!.shares ? 0.98 : 1 }}
                >
                  Verkaufen
                </motion.button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CompanionStockMarketModal; 